import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  MessageCircle, Phone, Video, Clock, Star, Sparkles, 
  Crown, Diamond, ChevronRight, Play, Users, Bot,
  Newspaper, FileText, Mic, MicOff, Volume2, VolumeX,
  Send, ArrowLeft, Settings, Heart, Zap, Download,
  User, Calendar, PhoneCall, Hand, Loader2, Wallet, Gift, Share2,
  Camera, Paperclip, X, Image as ImageIcon, Activity, Plus, Check,
  TrendingUp, Brain, Bell, PhoneCall as VideoCall, Globe, Eye
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa6';
import HealthTrendsDashboard from '../components/HealthTrendsDashboard';
import HealthInsights from '../components/HealthInsights';
import NotificationCenter, { NotificationBell } from '../components/NotificationCenter';
import VideoCallMode from '../components/VideoCallMode';
import VoiceRecorder from '../components/VoiceRecorder';
import DailyPanchangWidget from '../components/DailyPanchangWidget';

// Feature Toggle System for A/B Testing new components
import { useFeatureToggle, FEATURE_TOGGLES } from './sankat_mochan/featureToggles';
import ReportsView from './sankat_mochan/components/ReportsView';
import ConsultationView from './sankat_mochan/components/ConsultationView';
import PaymentView from './sankat_mochan/components/PaymentView';
import FreeTrialView from './sankat_mochan/components/FreeTrialView';
import HealthView from './sankat_mochan/components/HealthView';
import FeatureToggleAdmin from './sankat_mochan/components/FeatureToggleAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ ANIMATED AVATAR COMPONENT ============
const AnimatedAvatar = ({ avatar, isSpeaking = false, size = 'md', showVideoMode = false }) => {
  const sizes = {
    sm: 'w-12 h-12',
    md: 'w-20 h-20',
    lg: 'w-32 h-32',
    xl: 'w-44 h-44',
    '2xl': 'w-64 h-64'
  };
  
  const categoryColors = {
    religious: ['from-orange-500', 'to-amber-600', '#f97316'],
    professional: ['from-blue-500', 'to-cyan-600', '#3b82f6'],
    lifestyle: ['from-pink-500', 'to-rose-600', '#ec4899'],
    kids: ['from-green-500', 'to-emerald-600', '#22c55e'],
    universal: ['from-purple-500', 'to-indigo-600', '#8b5cf6'],
    custom: ['from-purple-500', 'to-violet-600', '#a855f7'],
  };
  
  const colors = categoryColors[avatar?.category] || categoryColors.religious;
  
  return (
    <div className={`relative ${sizes[size]}`}>
      {/* Outer glow ring when speaking */}
      {isSpeaking && (
        <>
          <div 
            className="absolute inset-[-8px] rounded-full animate-ping opacity-30"
            style={{ backgroundColor: colors[2] }}
          />
          <div 
            className="absolute inset-[-4px] rounded-full animate-pulse"
            style={{ 
              background: `radial-gradient(circle, ${colors[2]}60 0%, transparent 70%)`,
            }}
          />
          {/* Sound wave animation when speaking */}
          <div className="absolute -right-4 top-1/2 -translate-y-1/2 flex gap-0.5">
            {[...Array(3)].map((_, i) => (
              <div 
                key={i}
                className="w-1 bg-white/60 rounded-full animate-pulse"
                style={{ 
                  height: `${8 + i * 4}px`,
                  animationDelay: `${i * 150}ms`
                }}
              />
            ))}
          </div>
        </>
      )}
      
      {/* Main avatar container */}
      <div 
        className={`
          w-full h-full rounded-full overflow-hidden 
          bg-gradient-to-br ${colors[0]} ${colors[1]}
          flex items-center justify-center
          ${isSpeaking ? 'animate-pulse' : ''}
          transition-all duration-300
        `}
        style={{
          boxShadow: isSpeaking 
            ? `0 0 30px ${colors[2]}80, 0 0 60px ${colors[2]}40` 
            : `0 0 20px ${colors[2]}40`
        }}
      >
        {avatar?.image_url && !avatar.image_url.includes('pattern') ? (
          <div className="relative w-full h-full">
            <img 
              src={avatar.image_url} 
              alt={avatar?.display_name} 
              className={`w-full h-full object-cover ${isSpeaking ? 'scale-105' : 'scale-100'} transition-transform`}
            />
            {/* Lip sync effect overlay when speaking */}
            {isSpeaking && showVideoMode && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent flex items-end justify-center pb-[15%]">
                <div className="flex gap-1 items-end">
                  {[...Array(5)].map((_, i) => (
                    <div 
                      key={i}
                      className="w-1 bg-white/80 rounded-full animate-bounce"
                      style={{ 
                        height: `${4 + Math.random() * 8}px`,
                        animationDelay: `${i * 100}ms`,
                        animationDuration: '300ms'
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="relative w-full h-full flex items-center justify-center">
            <Bot className={`${size === 'xl' || size === '2xl' ? 'w-20 h-20' : size === 'lg' ? 'w-16 h-16' : 'w-10 h-10'} text-white`} />
            
            {/* Speaking mouth animation */}
            {isSpeaking && (
              <div className="absolute bottom-[25%] left-1/2 -translate-x-1/2">
                <div className="flex gap-0.5">
                  <div className="w-1 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '100ms' }} />
                  <div className="w-1 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Speaking indicator waves */}
      {isSpeaking && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
          {[...Array(3)].map((_, i) => (
            <div 
              key={i}
              className="w-1 bg-white rounded-full animate-bounce"
              style={{ 
                height: `${8 + i * 4}px`,
                animationDelay: `${i * 100}ms`,
                backgroundColor: colors[2]
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============ STYLES ============
const GlowButton = ({ children, onClick, className = "", variant = "primary", disabled = false }) => {
  const variants = {
    primary: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black",
    secondary: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white",
    gold: "bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 hover:from-yellow-300 text-black",
    diamond: "bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 hover:from-cyan-300 text-white",
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        px-6 py-3 rounded-xl font-bold transition-all duration-300 transform
        hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]} ${className}
      `}
      style={{
        boxShadow: `0 0 20px ${variant === 'gold' ? '#fbbf24' : variant === 'diamond' ? '#06b6d4' : '#f97316'}40`
      }}
    >
      {children}
    </button>
  );
};

// ============ AVATAR CARD ============
const AvatarCard = ({ avatar, selected, onSelect }) => {
  const categoryColors = {
    religious: 'from-orange-500 to-amber-600',
    professional: 'from-blue-500 to-cyan-600',
    lifestyle: 'from-pink-500 to-rose-600',
    kids: 'from-green-500 to-emerald-600',
    custom: 'from-purple-500 to-violet-600',
  };
  
  return (
    <div
      onClick={() => onSelect(avatar)}
      className={`
        relative p-4 rounded-2xl cursor-pointer transition-all duration-300
        ${selected ? 'ring-2 ring-amber-500 bg-amber-500/20' : 'bg-slate-800/50 hover:bg-slate-700/50'}
      `}
    >
      {/* Avatar Image */}
      <div className={`
        w-20 h-20 mx-auto rounded-full overflow-hidden mb-3
        bg-gradient-to-br ${categoryColors[avatar.category] || categoryColors.custom}
        flex items-center justify-center
      `}>
        {avatar.image_url && !avatar.image_url.includes('pattern') ? (
          <img src={avatar.image_url} alt={avatar.display_name} className="w-full h-full object-cover" />
        ) : (
          <Bot className="w-10 h-10 text-white" />
        )}
      </div>
      
      {/* Info */}
      <h3 className="text-white font-bold text-center text-sm mb-1">{avatar.display_name}</h3>
      <p className="text-gray-400 text-xs text-center line-clamp-2">{avatar.description}</p>
      
      {/* Category Badge */}
      <div className={`
        absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold
        bg-gradient-to-r ${categoryColors[avatar.category] || categoryColors.custom} text-white
      `}>
        {avatar.category}
      </div>
      
      {/* Premium Badge */}
      {avatar.is_premium && (
        <div className="absolute top-2 left-2">
          <Crown className="w-4 h-4 text-amber-400" />
        </div>
      )}
      
      {/* Selected Indicator */}
      {selected && (
        <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
          <span className="text-black text-xs">✓</span>
        </div>
      )}
    </div>
  );
};

// ============ PASS CARD ============
const PassCard = ({ pass, onSelect }) => {
  const passStyles = {
    silver: {
      gradient: 'from-gray-400 to-gray-600',
      icon: Star,
      glow: '#9ca3af'
    },
    golden: {
      gradient: 'from-yellow-400 to-amber-600',
      icon: Crown,
      glow: '#fbbf24'
    },
    diamond: {
      gradient: 'from-cyan-400 via-blue-500 to-purple-600',
      icon: Diamond,
      glow: '#06b6d4'
    }
  };
  
  const style = passStyles[pass.pass_type] || passStyles.silver;
  const Icon = style.icon;
  
  return (
    <div
      className={`
        relative p-6 rounded-2xl bg-gradient-to-br ${style.gradient}
        transform transition-all duration-300 hover:scale-105 cursor-pointer
      `}
      onClick={() => onSelect(pass)}
      style={{ boxShadow: `0 0 30px ${style.glow}40` }}
    >
      {/* Icon */}
      <div className="flex justify-center mb-4">
        <Icon className="w-12 h-12 text-white drop-shadow-lg" />
      </div>
      
      {/* Name */}
      <h3 className="text-2xl font-black text-white text-center mb-2 capitalize">
        {pass.pass_type} Pass
      </h3>
      
      {/* Price */}
      <p className="text-4xl font-black text-white text-center mb-4">
        ₹{pass.price}
      </p>
      
      {/* Duration */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-white/80" />
        <span className="text-white/80 font-medium">
          {pass.pass_type === 'diamond' 
            ? '3 Reports (Your Choice)' 
            : pass.duration_minutes === 999999 
              ? 'Unlimited' 
              : `${pass.duration_minutes} Minutes`}
        </span>
      </div>
      
      {/* Features */}
      <ul className="space-y-2 mb-6">
        {pass.features?.map((feature, idx) => (
          <li key={idx} className="flex items-center gap-2 text-white/90 text-sm">
            <Sparkles className="w-4 h-4" />
            {feature}
          </li>
        ))}
      </ul>
      
      {/* Select Button */}
      <button className="w-full py-3 bg-white/20 backdrop-blur-sm rounded-xl font-bold text-white hover:bg-white/30 transition-all">
        Select Pass
      </button>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const SankatMochanSamadhan = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('home'); // home, avatars, passes, payment, consultation, reports, trial, health, feature-toggles
  const [avatars, setAvatars] = useState([]);
  const [passes, setPasses] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedPass, setSelectedPass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userPasses, setUserPasses] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [aiVisibility, setAiVisibility] = useState({ samadhan_enabled: true, emergency_shutdown: false });
  
  // Feature Toggle Hooks for A/B Testing
  const useNewReports = useFeatureToggle(FEATURE_TOGGLES.USE_NEW_REPORTS_VIEW);
  const useNewConsultation = useFeatureToggle(FEATURE_TOGGLES.USE_NEW_CONSULTATION_VIEW);
  const useNewPayment = useFeatureToggle(FEATURE_TOGGLES.USE_NEW_PAYMENT_VIEW);
  const useNewTrial = useFeatureToggle(FEATURE_TOGGLES.USE_NEW_TRIAL_VIEW);
  const useNewHealth = useFeatureToggle(FEATURE_TOGGLES.USE_NEW_HEALTH_VIEW);
  const useAllNew = useFeatureToggle(FEATURE_TOGGLES.USE_ALL_NEW_COMPONENTS);
  
  // File Upload State
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  // Health History State
  const [healthSummary, setHealthSummary] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [healthSymptoms, setHealthSymptoms] = useState([]);
  const [healthMetrics, setHealthMetrics] = useState([]);
  const [healthProfile, setHealthProfile] = useState(null);
  const [showAddSymptom, setShowAddSymptom] = useState(false);
  const [showAddMetric, setShowAddMetric] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  // Quick Health Check State
  const [showQuickHealthCheck, setShowQuickHealthCheck] = useState(false);
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [healthCheckResult, setHealthCheckResult] = useState(null);
  // Health Trends State
  const [showHealthTrends, setShowHealthTrends] = useState(false);
  // Notification State
  const [showNotifications, setShowNotifications] = useState(false);
  // Export State
  const [exportingMessage, setExportingMessage] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceOption, setVoiceOption] = useState('auto'); // 'elevenlabs', 'openai', 'auto'
  const [videoAvatarSettings, setVideoAvatarSettings] = useState(null);
  const [language, setLanguage] = useState('hi');
  const [paymentRef, setPaymentRef] = useState('');
  const [news, setNews] = useState([]);
  const [newsCategory, setNewsCategory] = useState('india');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [avatarCategory, setAvatarCategory] = useState('all');
  const [userReports, setUserReports] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [reportForm, setReportForm] = useState({});
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportLanguage, setReportLanguage] = useState('hi'); // Default Hindi
  
  // Supported languages for reports
  const reportLanguages = [
    { code: 'hi', name: 'Hindi', native: 'Hindi' },
    { code: 'mr', name: 'Marathi', native: 'Marathi' },
    { code: 'gu', name: 'Gujarati', native: 'Gujarati' },
    { code: 'bn', name: 'Bengali', native: 'Bengali' },
    { code: 'ta', name: 'Tamil', native: 'Tamil' },
    { code: 'te', name: 'Telugu', native: 'Telugu' },
    { code: 'kn', name: 'Kannada', native: 'Kannada' },
    { code: 'ml', name: 'Malayalam', native: 'Malayalam' },
    { code: 'pa', name: 'Punjabi', native: 'Punjabi' },
    { code: 'en', name: 'English', native: 'English' },
  ];
  // Video Call Mode State
  const [videoCallActive, setVideoCallActive] = useState(false);
  const [currentAudioSrc, setCurrentAudioSrc] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  // Free Trial State
  const [trialStatus, setTrialStatus] = useState({ enabled: true, messages_used: 0, messages_remaining: 3, max_messages: 3 });
  const [trialMessages, setTrialMessages] = useState([]);
  const [trialId, setTrialId] = useState(null);
  // Referral System State
  const [referralData, setReferralData] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  // Panchang Widget State
  const [showPanchang, setShowPanchang] = useState(false);
  // 🆕 Full-report view modal
  const [viewReport, setViewReport] = useState(null);
  // 🔊 Read Aloud state (native speechSynthesis — zero cost)
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [speechRate, setSpeechRate] = useState(1.0);

  // Extract plain text from a report (strips markdown-lite formatting for TTS)
  const extractReportText = (r) => {
    if (!r) return '';
    const c = r.content;
    let text = '';
    if (typeof c === 'string') text = c;
    else if (c?.analysis && typeof c.analysis === 'string') text = c.analysis;
    else if (c?.text && typeof c.text === 'string') text = c.text;
    else if (c?.report && typeof c.report === 'string') text = c.report;
    if (!text) return '';
    return text
      .replace(/\*\*([^*]+)\*\*/g, '$1')     // strip bold markers
      .replace(/^#{1,3}\s+/gm, '')           // strip heading markers
      .replace(/[*_`~]/g, '')                 // strip stray md chars
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  // BCP-47 language map for TTS (report.language → speech lang)
  const speechLangFor = (lang) => ({
    hi: 'hi-IN', mr: 'mr-IN', en: 'en-IN', gu: 'gu-IN', bn: 'bn-IN',
    ta: 'ta-IN', te: 'te-IN', kn: 'kn-IN', ml: 'ml-IN', pa: 'pa-IN',
    ur: 'ur-IN', or: 'or-IN', as: 'as-IN',
  }[lang] || 'hi-IN');

  const startReadAloud = () => {
    if (!('speechSynthesis' in window)) {
      toast.error('Aapke browser mein Read Aloud support nahi hai');
      return;
    }
    const text = extractReportText(viewReport);
    if (!text) { toast.error('Padhne ke liye text nahi hai'); return; }
    window.speechSynthesis.cancel(); // stop any existing
    const targetLang = speechLangFor(viewReport?.language || 'hi');
    const voices = window.speechSynthesis.getVoices();
    // Prefer exact lang match, else lang prefix, else any
    const voice = voices.find((v) => v.lang === targetLang)
              || voices.find((v) => v.lang.startsWith(targetLang.split('-')[0]))
              || null;
    // Chunk long text (browsers stop at ~200 chars on some platforms) — split on sentence boundaries
    const chunks = text.match(/[^.!?।॥\n]+[.!?।॥\n]?/g) || [text];
    let i = 0;
    const speakNext = () => {
      if (i >= chunks.length) {
        setSpeaking(false); setPaused(false);
        return;
      }
      const u = new SpeechSynthesisUtterance(chunks[i].trim());
      u.lang = targetLang;
      u.rate = speechRate;
      u.pitch = 1.0;
      if (voice) u.voice = voice;
      u.onend = () => { i += 1; speakNext(); };
      u.onerror = () => { setSpeaking(false); setPaused(false); };
      window.speechSynthesis.speak(u);
    };
    setSpeaking(true); setPaused(false);
    speakNext();
    toast.success(`🔊 Padhna shuru — ${voice ? voice.name : targetLang}`);
  };

  const pauseReadAloud = () => {
    if (window.speechSynthesis?.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setPaused(true);
    } else if (window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
      setPaused(false);
    }
  };

  const stopReadAloud = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setSpeaking(false); setPaused(false);
  };

  // 📱 Save as Audio — captures speechSynthesis via tab-audio share (Chrome/Edge desktop only)
  const [saving, setSaving] = useState(false);
  const saveAsAudio = async () => {
    if (!('speechSynthesis' in window)) { toast.error('Browser mein Read Aloud support nahi hai'); return; }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error('Aapke browser mein audio recording support nahi hai — Chrome/Edge desktop use karo');
      return;
    }
    const text = extractReportText(viewReport);
    if (!text) { toast.error('Padhne ke liye text nahi hai'); return; }

    toast('📢 IMPORTANT: Popup mein "This tab" select karo AUR "Share tab audio" ka checkbox ✓ tick karo!', { duration: 8000 });
    setSaving(true);
    let stream = null;
    try {
      // Chrome/Edge: video track needed, but we only use audio
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: true,
        preferCurrentTab: true,
      });
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach((t) => t.stop());
        toast.error('Audio track nahi mila — "Share tab audio" checkbox tick karna zaroori hai');
        setSaving(false);
        return;
      }
      // Record only audio (drop video track)
      const audioStream = new MediaStream(audioTracks);
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const rec = new MediaRecorder(audioStream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mime || 'audio/webm' });
        if (blob.size < 500) {
          toast.error('Recording khali hai — "Share tab audio" checkbox miss ho gaya?');
          setSaving(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeTitle = (viewReport.title || viewReport.report_type || 'report').replace(/[^a-zA-Z0-9_\u0900-\u097F-]/g, '_').slice(0, 40);
        a.download = `${safeTitle}_${viewReport.language || 'audio'}.webm`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success(`💾 Audio saved (${(blob.size / 1024 / 1024).toFixed(1)} MB)`);
        setSaving(false);
      };

      // Start recording, then speech. When speech chunk queue empties, stop recorder.
      window.speechSynthesis.cancel();
      const targetLang = speechLangFor(viewReport?.language || 'hi');
      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find((v) => v.lang === targetLang)
                 || voices.find((v) => v.lang.startsWith(targetLang.split('-')[0]))
                 || null;
      const chunksTxt = text.match(/[^.!?।॥\n]+[.!?।॥\n]?/g) || [text];
      rec.start();
      setSpeaking(true);
      let i = 0;
      const speakNext = () => {
        if (i >= chunksTxt.length) {
          setSpeaking(false);
          setTimeout(() => { if (rec.state !== 'inactive') rec.stop(); }, 400);
          return;
        }
        const u = new SpeechSynthesisUtterance(chunksTxt[i].trim());
        u.lang = targetLang; u.rate = speechRate; u.pitch = 1.0;
        if (voice) u.voice = voice;
        u.onend = () => { i += 1; speakNext(); };
        u.onerror = () => { setSpeaking(false); if (rec.state !== 'inactive') rec.stop(); };
        window.speechSynthesis.speak(u);
      };
      speakNext();
    } catch (err) {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      window.speechSynthesis.cancel();
      setSpeaking(false); setSaving(false);
      if (err.name === 'NotAllowedError') toast('Recording cancelled', { icon: '⏹' });
      else toast.error(`Recording failed: ${err.message || err.name}`);
    }
  };

  // Stop speech when modal closes or component unmounts
  useEffect(() => {
    if (!viewReport && speaking) stopReadAloud();
    return () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); };
  }, [viewReport, speaking]);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  
  const token = localStorage.getItem('token');
  
  // Handle referral code from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('sankat_mochan_referral_code', refCode);
      // Remove ref param from URL without reload
      window.history.replaceState({}, '', window.location.pathname);
      toast.success('Referral code applied! Try our free messages.');
    }
  }, []);
  
  // Generate or get trial ID from localStorage
  useEffect(() => {
    let storedTrialId = localStorage.getItem('sankat_mochan_trial_id');
    if (!storedTrialId) {
      storedTrialId = `trial_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      localStorage.setItem('sankat_mochan_trial_id', storedTrialId);
    }
    setTrialId(storedTrialId);
    fetchTrialStatus(storedTrialId);
  }, []);
  
  // Fetch avatars
  useEffect(() => {
    fetchAvatars();
    fetchPasses();
    fetchNews();
    fetchAiVisibility(); // Check if service is enabled
    if (token) {
      fetchUserPasses();
      fetchUserReports();
      fetchReferralData();
      fetchReferralStats();
      fetchHealthSummary();
      fetchVideoAvatarSettings();
    }
  }, []);
  
  // Fetch AI visibility settings
  const fetchAiVisibility = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ai-control/public/avatar-visibility`);
      const data = await res.json();
      setAiVisibility(data);
    } catch (error) {
      console.log('Could not fetch AI visibility settings');
    }
  };
  
  const fetchVideoAvatarSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/video-avatar/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setVideoAvatarSettings(data);
      // Set default voice option based on availability
      if (data.voice_options?.elevenlabs?.available) {
        setVoiceOption('elevenlabs');
      } else if (data.voice_options?.openai?.available) {
        setVoiceOption('openai');
      }
    } catch (error) {
      console.error('Error fetching video avatar settings:', error);
    }
  };
  
  const fetchAvatars = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/avatars`);
      const data = await res.json();
      setAvatars(data.avatars || []);
    } catch (error) {
      console.error('Error fetching avatars:', error);
    }
  };
  
  const fetchPasses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/passes`);
      const data = await res.json();
      setPasses(data.passes || []);
    } catch (error) {
      console.error('Error fetching passes:', error);
    }
  };
  
  const fetchUserPasses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/my-passes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUserPasses(data.passes || []);
    } catch (error) {
      console.error('Error fetching user passes:', error);
    }
  };
  
  const fetchUserReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/my-reports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUserReports(data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };
  
  const fetchReferralData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/share-link`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    }
  };
  
  const fetchReferralStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/referral-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReferralStats(data);
      }
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };
  
  // Health History Functions
  const fetchHealthSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHealthSummary(data);
        setHealthProfile(data.profile);
      }
    } catch (error) {
      console.error('Error fetching health summary:', error);
    }
  };
  
  const fetchHealthHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/history?days=90&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHealthHistory(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching health history:', error);
    }
  };
  
  const fetchHealthSymptoms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/symptoms?active_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHealthSymptoms(data.symptoms || []);
      }
    } catch (error) {
      console.error('Error fetching symptoms:', error);
    }
  };
  
  const fetchHealthMetrics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/metrics?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHealthMetrics(data.metrics || []);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };
  
  const addSymptom = async (symptomData) => {
    try {
      const params = new URLSearchParams(symptomData);
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/symptoms?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Symptom added');
        fetchHealthSymptoms();
        fetchHealthSummary();
        setShowAddSymptom(false);
      }
    } catch (error) {
      toast.error('Failed to add symptom');
    }
  };
  
  const addMetric = async (metricData) => {
    try {
      const params = new URLSearchParams({
        metric_type: metricData.metric_type || metricData.type,
        value: metricData.value,
        unit: metricData.unit || '',
        notes: metricData.notes || ''
      });
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/metrics?${params}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        toast.success('Metric recorded successfully!');
        fetchHealthMetrics();
        fetchHealthSummary();
        setShowAddMetric(false);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to add metric');
      }
    } catch (error) {
      toast.error('Failed to add metric');
    }
  };
  
  const resolveSymptom = async (symptomId) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/symptoms/${symptomId}?is_active=false`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Symptom marked as resolved');
        fetchHealthSymptoms();
        fetchHealthSummary();
      }
    } catch (error) {
      toast.error('Failed to update symptom');
    }
  };

  // Quick Health Check Functions
  const performQuickHealthCheck = async (imageFile) => {
    if (!token) {
      toast.error('Please login to use Quick Health Check');
      return;
    }
    
    setHealthCheckLoading(true);
    setHealthCheckResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('language', language);
      formData.append('save_record', 'true');
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/quick-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setHealthCheckResult(data);
        toast.success('Health check complete!');
        fetchHealthHistory();
        fetchHealthSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Health check failed');
      }
    } catch (error) {
      toast.error('Health check failed: ' + error.message);
    } finally {
      setHealthCheckLoading(false);
    }
  };

  const captureHealthCheckPhoto = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera not supported on this device');
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      setCameraStream(stream);
      setShowQuickHealthCheck(true);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      toast.error('Could not access camera');
    }
  };

  const takeHealthCheckSnapshot = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], 'health_check.jpg', { type: 'image/jpeg' });
        await performQuickHealthCheck(file);
      }
    }, 'image/jpeg', 0.85);
    
    // Stop camera
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowQuickHealthCheck(false);
  };

  // Export Functions
  const exportToPDF = async (messageContent, question = '', isFullChat = false, sessionId = null) => {
    if (!token) return;
    
    try {
      setExportingMessage('pdf');
      const formData = new FormData();
      
      if (isFullChat && sessionId) {
        formData.append('session_id', sessionId);
      } else {
        formData.append('message_content', messageContent);
        if (question) formData.append('question', question);
      }
      
      formData.append('avatar_name', selectedAvatar?.display_name || 'AI Assistant');
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/export/pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `consultation_${Date.now()}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded!');
      } else {
        toast.error('Failed to export PDF');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExportingMessage(null);
    }
  };

  const exportToAudio = async (messageContent) => {
    if (!token) return;
    
    try {
      setExportingMessage('audio');
      const formData = new FormData();
      formData.append('message_content', messageContent);
      formData.append('language', language);
      formData.append('avatar_name', selectedAvatar?.display_name || 'AI Assistant');
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/export/audio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `response_${Date.now()}.mp3`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Audio downloaded!');
      } else {
        toast.error('Failed to export audio');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExportingMessage(null);
    }
  };

  const exportToVideo = async (messageContent) => {
    if (!token || !selectedAvatar) return;
    
    try {
      setExportingMessage('video');
      const formData = new FormData();
      formData.append('message_content', messageContent);
      formData.append('avatar_id', selectedAvatar.id);
      formData.append('language', language);
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/export/video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.info('Video generation started! This may take a few minutes.');
        // Could add polling logic here for video status
      } else {
        toast.error('Failed to start video export');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExportingMessage(null);
    }
  };
  
  const copyReferralLink = () => {
    if (referralData?.share_link) {
      navigator.clipboard.writeText(referralData.share_link);
      toast.success('Referral link copied!');
    }
  };
  
  const shareToWhatsApp = () => {
    if (referralData?.share_message) {
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(referralData.share_message)}`;
      window.open(whatsappUrl, '_blank');
    }
  };
  
  const fetchNews = async (category = 'india') => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/news?category=${category}&limit=10`);
      const data = await res.json();
      setNews(data.news || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  };
  
  // Fetch free trial status
  const fetchTrialStatus = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/free-trial/status?trial_id=${id}`);
      const data = await res.json();
      setTrialStatus(data);
    } catch (error) {
      console.error('Error fetching trial status:', error);
    }
  };
  
  // Send free trial message
  const sendTrialMessage = async () => {
    if (!inputMessage.trim() || !selectedAvatar || !trialId) return;
    
    if (trialStatus.messages_remaining <= 0) {
      toast.error('Free trial ended! Purchase a pass to continue.');
      setCurrentView('passes');
      return;
    }
    
    const userMsg = inputMessage;
    setInputMessage('');
    setTrialMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    
    try {
      // Get referral code from localStorage if exists
      const referralCode = localStorage.getItem('sankat_mochan_referral_code');
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/free-trial/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          avatar_id: selectedAvatar.id,
          message: userMsg,
          language: language,
          trial_id: trialId,
          referral_code: referralCode || undefined,
          generate_voice: voiceEnabled
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        setTrialMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.message,
          audio: data.audio_base64
        }]);
        
        // Update trial status
        setTrialStatus(prev => ({
          ...prev,
          messages_used: data.messages_used,
          messages_remaining: data.messages_remaining
        }));
        
        // Play audio if voice is enabled and audio is available
        if (data.audio_base64 && voiceEnabled) {
          playAudio(data.audio_base64);
        }
        
        if (data.trial_expired) {
          toast.info('Free trial ended! Purchase a pass to continue your journey.', { duration: 5000 });
        }
      } else {
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Network error');
    }
    setLoading(false);
  };
  
  // Start free trial session
  const startFreeTrial = () => {
    if (!selectedAvatar) {
      toast.error('Please select an avatar first');
      setCurrentView('avatars');
      return;
    }
    setTrialMessages([]);
    setCurrentView('trial');
  };
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('manual'); // manual, wallet, hybrid
  const [walletBalance, setWalletBalance] = useState(0);
  const [commissionBalance, setCommissionBalance] = useState(0);
  
  // Fetch user wallet balance
  const fetchWalletBalance = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/wallet/balances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setWalletBalance(data.cash_wallet || 0);
      setCommissionBalance(data.commission_wallet || 0);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };
  
  useEffect(() => {
    if (token) fetchWalletBalance();
  }, [token]);
  
  const handlePurchasePass = async () => {
    if (!token) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    
    if (!selectedPass) {
      toast.error('Please select a pass');
      return;
    }
    
    // Validate based on payment method
    if (paymentMethod === 'manual' && !paymentRef.trim()) {
      toast.error('Please enter payment reference/UTR number');
      return;
    }
    
    const passPrice = selectedPass.price || 0;
    
    if (paymentMethod === 'wallet' && walletBalance < passPrice) {
      toast.error(`Insufficient wallet balance. Required: ₹${passPrice}`);
      return;
    }
    
    if (paymentMethod === 'hybrid' && walletBalance < passPrice * 0.5) {
      toast.error(`Insufficient wallet balance. Required: ₹${passPrice * 0.5} (50%)`);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/purchase-pass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pass_type: selectedPass.pass_type,
          payment_method: paymentMethod,
          payment_reference: paymentRef
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        if (paymentMethod === 'manual') {
          toast.success('Pass purchase submitted! Waiting for admin approval.');
        } else {
          toast.success('Pass activated! You can start your session now.');
          fetchWalletBalance(); // Refresh balance
        }
        fetchUserPasses();
        setCurrentView('home');
        setPaymentRef('');
        setPaymentMethod('manual');
      } else {
        toast.error(data.detail || 'Purchase failed');
      }
    } catch (error) {
      toast.error('Network error');
    }
    setLoading(false);
  };
  
  // Session timer state
  const [sessionTimeLimit, setSessionTimeLimit] = useState(30);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(30);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const timerRef = useRef(null);
  
  // Timer effect - updates every second when in consultation
  useEffect(() => {
    if (currentView === 'consultation' && sessionStartTime && activeSession) {
      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - sessionStartTime) / 1000 / 60; // minutes
        const remaining = Math.max(0, sessionTimeLimit - elapsed);
        setSessionTimeRemaining(remaining);
        
        // Auto-end session when time is up
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          toast.error('Session time expired! Please purchase a new pass to continue.');
          endSession();
        }
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    }
  }, [currentView, sessionStartTime, sessionTimeLimit, activeSession]);
  
  // Redeem coupon
  const [couponCode, setCouponCode] = useState('');
  
  const handleRedeemCoupon = async () => {
    if (!token) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    
    if (!couponCode.trim()) {
      toast.error('Please enter coupon code');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/redeem-coupon?coupon_code=${couponCode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Coupon redeemed!');
        fetchUserPasses();
        setCouponCode('');
        setCurrentView('home');
      } else {
        toast.error(data.detail || 'Invalid coupon');
      }
    } catch (error) {
      toast.error('Network error');
    }
    setLoading(false);
  };
  
  const startSession = async (passId) => {
    if (!selectedAvatar) {
      toast.error('Please select an avatar first');
      setCurrentView('avatars');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/start-session?pass_id=${passId}&avatar_id=${selectedAvatar.id}&language=${language}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (res.ok) {
        setActiveSession(data.session);
        setMessages([]);
        // Set timer based on pass type
        const timeLimit = data.time_limit_minutes || data.session?.session_duration_minutes || 30;
        setSessionTimeLimit(timeLimit);
        setSessionTimeRemaining(timeLimit);
        setSessionStartTime(Date.now());
        setCurrentView('consultation');
        toast.success(`Session started with ${selectedAvatar.display_name} (${timeLimit} min)`);
      } else {
        toast.error(data.detail || 'Failed to start session');
      }
    } catch (error) {
      toast.error('Network error');
    }
    setLoading(false);
  };
  
  // File Upload Functions
  const handleFileUpload = async (file) => {
    if (!file || !activeSession) return;
    
    setUploadingFile(true);
    setShowFileUpload(false);
    
    // Show user message with file info
    const fileType = file.type.split('/')[0];
    const fileIcon = fileType === 'image' ? '🖼️' : fileType === 'audio' ? '🎵' : fileType === 'video' ? '🎬' : '📄';
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: `${fileIcon} Uploaded: ${file.name}`,
      isFile: true,
      fileName: file.name
    }]);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question', inputMessage || 'Please analyze this and provide your guidance');
      formData.append('avatar_id', selectedAvatar?.id || '');
      formData.append('language', language);
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/analyze-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        let responseContent = data.response;
        if (data.transcript) {
          responseContent = `🎤 I heard: "${data.transcript}"\n\n${data.response}`;
        }
        
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: responseContent,
          fileAnalysis: true
        }]);
        
        if (data.session_time_remaining) {
          setSessionTimeRemaining(Math.floor(data.session_time_remaining / 60));
        }
      } else {
        toast.error(data.detail || 'Failed to analyze file');
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    }
    
    setUploadingFile(false);
    setInputMessage('');
  };
  
  // Camera Functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Could not access camera. Please check permissions.');
    }
  };
  
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };
  
  const capturePhoto = async (analysisType = 'general') => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    // Get base64 image
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    const base64Data = imageData.split(',')[1];
    
    stopCamera();
    setUploadingFile(true);
    
    // Show user message
    const analysisLabels = {
      general: '📸 Selfie for analysis',
      palm: '🖐️ Palm image for palmistry',
      face: '😊 Face for reading',
      health: '💚 Health check image',
      aura: '✨ Aura analysis'
    };
    
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: analysisLabels[analysisType] || '📸 Camera capture',
      isFile: true
    }]);
    
    try {
      const formData = new FormData();
      formData.append('image_base64', base64Data);
      formData.append('analysis_type', analysisType);
      formData.append('avatar_id', selectedAvatar?.id || '');
      formData.append('language', language);
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/analyze-camera`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response,
          fileAnalysis: true
        }]);
        
        if (data.session_time_remaining) {
          setSessionTimeRemaining(Math.floor(data.session_time_remaining / 60));
        }
      } else {
        toast.error(data.detail || 'Failed to analyze image');
      }
    } catch (error) {
      console.error('Camera capture error:', error);
      toast.error('Failed to analyze captured image');
    }
    
    setUploadingFile(false);
  };
  
  // Free trial file upload (image only)
  const handleTrialFileUpload = async (file) => {
    if (!file || !selectedAvatar) return;
    
    // Only images allowed in free trial
    if (!file.type.startsWith('image/')) {
      toast.error('Free trial only supports image uploads. Purchase a pass for audio/video.');
      return;
    }
    
    setLoading(true);
    setTrialMessages(prev => [...prev, { 
      role: 'user', 
      content: `🖼️ Uploaded: ${file.name}`,
      isFile: true
    }]);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question', inputMessage || 'Please analyze this image');
      formData.append('avatar_id', selectedAvatar.id);
      formData.append('language', language);
      formData.append('trial_id', trialId);
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/free-trial/analyze-file`, {
        method: 'POST',
        body: formData
      });
      
      const data = await res.json();
      
      if (data.success) {
        setTrialMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response
        }]);
        
        setTrialStatus(prev => ({
          ...prev,
          messages_used: data.messages_used,
          messages_remaining: data.messages_remaining
        }));
        
        if (data.trial_expired) {
          toast.info('Free trial completed! Purchase a pass for unlimited access.');
        }
      } else {
        toast.error(data.message || 'Failed to analyze image');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    }
    
    setLoading(false);
    setInputMessage('');
  };
  
  const sendMessage = async () => {
    if (!inputMessage.trim() || !activeSession) return;
    
    // Check if session time expired
    if (sessionTimeRemaining <= 0) {
      toast.error('Session time expired! Please purchase a new pass.');
      return;
    }
    
    const userMsg = inputMessage;
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: activeSession.id,
          message: userMsg,
          generate_voice: voiceEnabled,
          language: language
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.message,
          audio: data.audio_base64
        }]);
        
        // Update time remaining from server
        if (data.minutes_remaining !== undefined) {
          setSessionTimeRemaining(data.minutes_remaining);
        }
        
        // Show time warning if present
        if (data.time_warning) {
          toast.warning(data.time_warning);
        }
        
        // Play audio if available
        if (data.audio_base64 && voiceEnabled) {
          playAudio(data.audio_base64);
        }
      } else {
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Network error');
    }
    setLoading(false);
  };
  
  const playAudio = (base64Audio) => {
    try {
      const audioSrc = `data:audio/mp3;base64,${base64Audio}`;
      
      // If video call mode is active, pass audio to VideoCallMode
      if (videoCallActive) {
        setCurrentAudioSrc(audioSrc);
        setIsAudioPlaying(true);
        setIsSpeaking(true);
        return;
      }
      
      // Normal audio playback
      const audio = new Audio(audioSrc);
      setIsSpeaking(true);
      
      audio.onplay = () => setIsSpeaking(true);
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = () => setIsSpeaking(false);
      audio.onpause = () => setIsSpeaking(false);
      
      audio.play().catch(err => {
        console.error('Audio play error:', err);
        // Mobile browsers may block autoplay
        toast.info('Tap to play audio', { duration: 2000 });
      });
      
      // Auto stop speaking animation after estimated duration
      setTimeout(() => setIsSpeaking(false), 30000);
    } catch (error) {
      console.error('Audio playback error:', error);
      setIsSpeaking(false);
    }
  };
  
  // Handle audio end in video call mode
  const handleVideoCallAudioEnd = () => {
    setIsAudioPlaying(false);
    setIsSpeaking(false);
  };
  
  // Start video call mode
  const startVideoCallMode = () => {
    if (!selectedAvatar) {
      toast.error('Please select an avatar first');
      return;
    }
    setVideoCallActive(true);
    toast.success(`Video call started with ${selectedAvatar?.display_name}`, { duration: 2000 });
  };
  
  const endSession = async () => {
    if (!activeSession) return;
    
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/end-session/${activeSession.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      setActiveSession(null);
      setMessages([]);
      setSessionStartTime(null);
      setCurrentView('home');
      
      if (data.need_new_pass) {
        toast.info(`Session ended (${data.duration_minutes?.toFixed(1)} min). You'll need a new pass for your next session.`, { duration: 5000 });
      } else {
        toast.success('Session ended');
      }
      fetchUserPasses();
    } catch (error) {
      toast.error('Failed to end session');
    }
  };
  
  // Format time for display
  const formatTime = (minutes) => {
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Generate report
  const generateReport = async (reportType) => {
    if (!token) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    
    setGeneratingReport(true);
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/generate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          report_type: reportType,
          language: reportLanguage, // Include selected language
          ...reportForm
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        const langName = reportLanguages.find(l => l.code === reportLanguage)?.native || 'Hindi';
        toast.success(`Report generated in ${langName}! ${data.reports_remaining} reports remaining.`);
        fetchUserReports();
        fetchUserPasses(); // CRITICAL: Refresh passes to update reports_used counter
        setReportForm({});
        setSelectedReportType(null);
      } else {
        toast.error(data.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Network error');
    }
    setGeneratingReport(false);
  };
  
  // Download report PDF
  const downloadReportPDF = async (reportId) => {
    if (!reportId) {
      toast.error('Invalid report ID');
      return;
    }
    // Ensure we have a fresh token from localStorage (not stale closure)
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      toast.error('Please login again');
      return;
    }
    const tid = toast.loading('Preparing PDF…');
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/report/${reportId}/download`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        // Verify blob is actually a PDF (size + type)
        if (blob.size < 100) {
          toast.dismiss(tid);
          toast.error('PDF empty — try regenerating the report');
          return;
        }
        // Extract filename from Content-Disposition if server provides one
        const cd = res.headers.get('content-disposition') || '';
        const m = cd.match(/filename="?([^"\n;]+)"?/i);
        const filename = m ? m[1] : `report_${reportId.slice(0, 8)}.pdf`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        toast.dismiss(tid);
        toast.success('PDF downloaded!');
      } else {
        // Show actual server error
        let errMsg = `Server error (${res.status})`;
        try {
          const data = await res.json();
          errMsg = data.detail || errMsg;
        } catch { /* non-JSON error */ }
        toast.dismiss(tid);
        if (res.status === 401 || res.status === 403) {
          toast.error('Session expired — please login again');
        } else if (res.status === 404) {
          toast.error('Report not found — it may have been deleted');
        } else {
          toast.error(errMsg);
        }
        console.error('PDF download failed:', res.status, errMsg);
      }
    } catch (error) {
      toast.dismiss(tid);
      console.error('PDF download exception:', error);
      toast.error(`Download failed: ${error.message || 'network error'}`);
    }
  };
  
  // Share report on WhatsApp
  const shareOnWhatsApp = (report) => {
    const title = report.title || report.report_type?.replace(/_/g, ' ').toUpperCase();
    const langName = report.language_name || 'Hindi';
    const preview = report.content?.analysis?.substring(0, 200) || '';
    
    const message = `🔮 *${title}*\n\n` +
      `📜 Generated in: ${langName}\n\n` +
      `${preview}...\n\n` +
      `✨ Get your personalized AI reports at ANANT WEB PORTAL - Sankat Mochan Samadhan\n` +
      `🌐 ${window.location.origin}`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };
  
  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const filteredAvatars = avatarCategory === 'all' 
    ? avatars 
    : avatars.filter(a => a.category === avatarCategory);
  
  const confirmedPasses = userPasses.filter(p => p.status === 'active' && p.sessions_used < 1);
  const diamondPasses = userPasses.filter(p => p.status === 'active' && p.pass_type === 'diamond' && (p.reports_limit - p.reports_used) > 0);
  
  // ============ RENDER VIEWS ============
  
  // Check if service is disabled by admin
  if (aiVisibility.emergency_shutdown || !aiVisibility.samadhan_enabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">सेवा अस्थायी रूप से बंद है</h2>
          <p className="text-muted-foreground mb-6">
            Sankat Mochan Samadhan service is temporarily unavailable. Please check back later.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/80 transition-all"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }
  
  // FEATURE TOGGLE ADMIN VIEW
  if (currentView === 'feature-toggles') {
    return <FeatureToggleAdmin onBack={() => setCurrentView('home')} />;
  }
  
  // REPORTS VIEW - Use new component if feature toggle enabled
  if (currentView === 'reports' && (useNewReports || useAllNew)) {
    return (
      <ReportsView
        onBack={() => { setCurrentView('home'); setSelectedReportType(null); }}
        userPasses={userPasses}
        userReports={userReports}
        token={token}
        onNavigateToPasses={() => setCurrentView('passes')}
        fetchUserReports={fetchUserReports}
      />
    );
  }
  
  // TRIAL VIEW - Use new component if feature toggle enabled
  if (currentView === 'trial' && (useNewTrial || useAllNew)) {
    return (
      <FreeTrialView
        onBack={() => setCurrentView('home')}
        avatars={avatars}
        trialStatus={trialStatus}
        setTrialStatus={setTrialStatus}
        onBuyPass={() => setCurrentView('passes')}
      />
    );
  }
  
  // HEALTH VIEW - Use new component if feature toggle enabled
  if (currentView === 'health' && (useNewHealth || useAllNew)) {
    return (
      <HealthView
        onBack={() => setCurrentView('home')}
        token={token}
        healthSummary={healthSummary}
        healthHistory={healthHistory}
        healthSymptoms={healthSymptoms}
        healthMetrics={healthMetrics}
        fetchHealthSymptoms={fetchHealthSymptoms}
        fetchHealthHistory={fetchHealthHistory}
        onViewInsights={() => setCurrentView('insights')}
        onViewTrends={() => setCurrentView('trends')}
      />
    );
  }
  
  // HOME VIEW
  if (currentView === 'home') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
        {/* Notification Bell - Fixed Position */}
        {token && (
          <div className="fixed top-20 right-6 z-50">
            <div className="bg-slate-800/90 backdrop-blur-sm rounded-full p-1 shadow-lg border border-purple-500/30">
              <NotificationBell 
                token={token} 
                onClick={() => setShowNotifications(true)} 
              />
            </div>
          </div>
        )}
        
        {/* Notification Center Modal */}
        <NotificationCenter 
          token={token}
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
        />
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">24/7 AI Consultation</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-500">
              SANKAT MOCHAN
            </span>
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">SAMADHAN</h2>
          <p className="text-gray-400 max-w-lg mx-auto">
            "Sankat Khatam, Samadhan Shuru" - Your 24/7 AI-powered consultation platform
            with religious gurus, professionals, and lifestyle advisors.
          </p>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto">
          <div className="bg-slate-800/50 rounded-xl p-4 text-center">
            <Bot className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{avatars.length}+</p>
            <p className="text-gray-400 text-sm">AI Avatars</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 text-center">
            <MessageCircle className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">24/7</p>
            <p className="text-gray-400 text-sm">Available</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 text-center">
            <Volume2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">Voice</p>
            <p className="text-gray-400 text-sm">Hindi & English</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 text-center">
            <FileText className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">PDF</p>
            <p className="text-gray-400 text-sm">Reports</p>
          </div>
        </div>
        
        {/* Action Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-8">
          {/* 10-min VIP Paid Session — premium spotlight */}
          <div
            onClick={() => navigate('/sankat/paid')}
            className="relative overflow-hidden bg-gradient-to-br from-amber-500/30 via-orange-500/25 to-rose-500/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border-2 border-amber-400 shadow-lg shadow-amber-500/20 lg:col-span-2"
            data-testid="vip-paid-session-card"
          >
            <div className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-extrabold rounded-full bg-amber-500 text-black uppercase tracking-wider">NEW · PREMIUM</div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center shadow-md">
                <Sparkles className="w-8 h-8 text-black" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl sm:text-2xl font-extrabold text-amber-200 leading-tight">10-min VIP Consultation</h3>
                <p className="text-amber-100/80 text-sm mt-1">
                  Pay <span className="font-bold text-amber-300">200 AT</span> · Full 10-minute 1-on-1 with your chosen avatar · Personalized tips, sponsored solutions, deep emotional support.
                </p>
                <div className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-black text-sm font-extrabold">
                  Start Now <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>

          {/* Start Consultation */}
          <div 
            onClick={() => setCurrentView('avatars')}
            className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-purple-500/30"
            data-testid="choose-avatar-card"
          >
            <Bot className="w-12 h-12 text-purple-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Choose Avatar</h3>
            <p className="text-gray-400 text-sm mb-4">
              Select from religious gurus, professionals, lifestyle advisors & more
            </p>
            <div className="flex items-center text-purple-400">
              <span>Browse Avatars</span>
              <ChevronRight className="w-5 h-5" />
            </div>
          </div>
          
          {/* Buy Pass */}
          <div 
            onClick={() => setCurrentView('passes')}
            className="bg-gradient-to-br from-amber-600/30 to-orange-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-amber-500/30"
            data-testid="buy-pass-card"
          >
            <Crown className="w-12 h-12 text-amber-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Buy Pass</h3>
            <p className="text-gray-400 text-sm mb-4">
              Silver, Golden & Diamond passes for consultations & reports
            </p>
            <div className="flex items-center text-amber-400">
              <span>View Passes</span>
              <ChevronRight className="w-5 h-5" />
            </div>
          </div>
          
          {/* AI Reports */}
          <div 
            onClick={() => setCurrentView('reports')}
            className="bg-gradient-to-br from-cyan-600/30 to-teal-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-cyan-500/30"
            data-testid="ai-reports-card"
          >
            <FileText className="w-12 h-12 text-cyan-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">AI Reports</h3>
            <p className="text-gray-400 text-sm mb-4">
              Numerology, Kundali, Palmistry & Match Making reports
            </p>
            <div className="flex items-center text-cyan-400">
              <span>Generate Reports</span>
              <ChevronRight className="w-5 h-5" />
            </div>
          </div>
          
          {/* Latest News */}
          <div 
            onClick={() => {
              fetchNews(newsCategory);
              document.getElementById('news-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-gradient-to-br from-blue-600/30 to-indigo-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-blue-500/30"
            data-testid="news-card"
          >
            <Newspaper className="w-12 h-12 text-blue-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Latest News</h3>
            <p className="text-gray-400 text-sm mb-4">
              Real-time news on India, Business, Astrology & Stock Market
            </p>
            <div className="flex items-center text-blue-400">
              <span>Read News</span>
              <ChevronRight className="w-5 h-5" />
            </div>
          </div>
          
          {/* Referral Card - Show only when logged in */}
          {token && (
            <div 
              onClick={() => setShowReferralModal(true)}
              className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-green-500/30"
              data-testid="referral-card"
            >
              <Gift className="w-12 h-12 text-green-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Refer & Earn</h3>
              <p className="text-gray-400 text-sm mb-4">
                Invite friends & earn rewards when they join!
              </p>
              <div className="flex items-center text-green-400">
                <span>Share Now</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          )}
          
          {/* Health History Card - Show only when logged in */}
          {token && (
            <div 
              onClick={() => {
                setCurrentView('health');
                fetchHealthHistory();
                fetchHealthSymptoms();
                fetchHealthMetrics();
              }}
              className="bg-gradient-to-br from-red-600/30 to-pink-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-red-500/30"
              data-testid="health-history-card"
            >
              <Activity className="w-12 h-12 text-red-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Health History</h3>
              <p className="text-gray-400 text-sm mb-4">
                Track symptoms, prescriptions & health progress
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-red-400">
                  <span>View History</span>
                  <ChevronRight className="w-5 h-5" />
                </div>
                {healthSummary && (
                  <div className="flex gap-2 text-xs">
                    <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded-full">
                      {healthSummary.active_symptoms} symptoms
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Health Trends Card - Show only when logged in */}
          {token && (
            <div 
              onClick={() => setCurrentView('trends')}
              className="bg-gradient-to-br from-indigo-600/30 to-purple-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-indigo-500/30"
              data-testid="health-trends-card"
            >
              <TrendingUp className="w-12 h-12 text-indigo-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Health Trends</h3>
              <p className="text-gray-400 text-sm mb-4">
                View wellness scores, metrics & symptom trends over time
              </p>
              <div className="flex items-center text-indigo-400">
                <span>View Trends</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          )}
          
          {/* Health Insights Card - Show only when logged in */}
          {token && (
            <div 
              onClick={() => setCurrentView('insights')}
              className="bg-gradient-to-br from-violet-600/30 to-fuchsia-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-violet-500/30"
              data-testid="health-insights-card"
            >
              <Brain className="w-12 h-12 text-violet-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Health Insights</h3>
              <p className="text-gray-400 text-sm mb-4">
                AI-powered analysis & personalized recommendations
              </p>
              <div className="flex items-center text-violet-400">
                <span>View Insights</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          )}
          
          {/* Daily Panchang & Horoscope Card */}
          <div 
            onClick={() => setShowPanchang(true)}
            className="bg-gradient-to-br from-orange-600/30 to-amber-600/30 rounded-2xl p-6 cursor-pointer hover:scale-105 transition-all border border-orange-500/30"
            data-testid="panchang-card"
          >
            <Calendar className="w-12 h-12 text-orange-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Daily Panchang</h3>
            <p className="text-gray-400 text-sm mb-4">
              Today's Tithi, Nakshatra, Yoga & Horoscope
            </p>
            <div className="flex items-center text-orange-400">
              <span>View Today</span>
              <ChevronRight className="w-5 h-5" />
            </div>
          </div>
        </div>
        
        {/* Daily Panchang Modal */}
        {showPanchang && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPanchang(false)}>
            <div className="max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-end mb-2">
                <button onClick={() => setShowPanchang(false)} className="text-white/70 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <DailyPanchangWidget />
            </div>
          </div>
        )}
        
        {/* My Active Passes */}
        {confirmedPasses.length > 0 && (
          <div className="max-w-5xl mx-auto mb-8">
            <h3 className="text-xl font-bold text-white mb-4">Your Active Passes</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {confirmedPasses.map(pass => (
                <div key={pass.id} className="bg-slate-800/50 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-white capitalize">{pass.pass_type} Pass</p>
                    <p className="text-gray-400 text-sm">{pass.minutes_remaining?.toFixed(1)} minutes remaining</p>
                  </div>
                  <GlowButton onClick={() => startSession(pass.id)} variant="primary">
                    Start Session
                  </GlowButton>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Platform Guide & Feature Toggles */}
        <div className="max-w-5xl mx-auto mb-8">
          <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border border-indigo-500/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Platform Guide & Documentation</h3>
                <p className="text-gray-400 text-sm">Download the complete system documentation with tutorials for all features</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    toast.loading('Generating PDF...');
                    try {
                      const res = await fetch(`${API_URL}/api/sankat-mochan/platform-guide/download`);
                      if (res.ok) {
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `ANANT_WebPortal_Guide_${new Date().toISOString().split('T')[0]}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.dismiss();
                        toast.success('Guide downloaded!');
                      } else {
                        throw new Error('Download failed');
                      }
                    } catch (error) {
                      toast.dismiss();
                      toast.error('Failed to download guide');
                    }
                  }}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:opacity-90 transition-all"
                  data-testid="download-platform-guide-btn"
                >
                  <Download className="w-5 h-5" />
                  Download PDF Guide
                </button>
                {token && (
                  <button
                    onClick={() => setCurrentView('feature-toggles')}
                    className="flex items-center gap-2 px-4 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all"
                    data-testid="feature-toggles-btn"
                  >
                    <Settings className="w-5 h-5" />
                    Features
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* News Section */}
        <div id="news-section" className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Latest News</h3>
            <select
              value={newsCategory}
              onChange={(e) => {
                setNewsCategory(e.target.value);
                fetchNews(e.target.value);
              }}
              className="bg-slate-800 text-white px-4 py-2 rounded-lg"
            >
              <option value="india">India</option>
              <option value="business">Business</option>
              <option value="astrology">Astrology</option>
              <option value="stock_market">Stock Market</option>
              <option value="spiritual">Spiritual</option>
              <option value="world">World</option>
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {news.slice(0, 6).map((item, idx) => (
              <a
                key={idx}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-800/50 rounded-xl p-4 hover:bg-slate-700/50 transition-all"
              >
                <p className="text-white font-medium line-clamp-2 mb-2">{item.title}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs">{item.source}</span>
                  <span className="text-gray-500 text-xs">{item.published}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // AVATARS VIEW
  if (currentView === 'avatars') {
    const categories = ['all', 'religious', 'professional', 'lifestyle', 'kids', 'universal'];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setCurrentView('home')} className="text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Choose Your Guide</h1>
            <p className="text-gray-400 text-sm">Select an avatar for your consultation</p>
          </div>
        </div>
        
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setAvatarCategory(cat)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all
                ${avatarCategory === cat 
                  ? 'bg-amber-500 text-black' 
                  : 'bg-slate-800 text-white hover:bg-slate-700'}
              `}
            >
              {cat === 'all' ? 'All Avatars' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Avatars Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          {filteredAvatars.map(avatar => (
            <AvatarCard
              key={avatar.id}
              avatar={avatar}
              selected={selectedAvatar?.id === avatar.id}
              onSelect={setSelectedAvatar}
            />
          ))}
        </div>
        
        {/* Continue Button */}
        {selectedAvatar && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900/90 backdrop-blur-lg border-t border-slate-800">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center overflow-hidden">
                  {selectedAvatar.image_url ? (
                    <img src={selectedAvatar.image_url} alt={selectedAvatar.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <Bot className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-white font-bold">{selectedAvatar.display_name}</p>
                  <p className="text-gray-400 text-sm">{selectedAvatar.category}</p>
                </div>
              </div>
              <div className="flex gap-3">
                {/* Free Trial Button — auto-hides since free_trial_enabled=False */}
                {trialStatus.enabled && trialStatus.messages_remaining > 0 && (
                  <button
                    onClick={startFreeTrial}
                    className="px-5 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold hover:from-green-400 hover:to-emerald-500 transition-all flex items-center gap-2"
                    data-testid="try-free-btn"
                  >
                    <Zap className="w-4 h-4" />
                    Try Free ({trialStatus.messages_remaining} msgs)
                  </button>
                )}
                {/* NEW · 10-min VIP Paid Session CTA */}
                <button
                  onClick={() => navigate('/sankat/paid')}
                  data-testid="sankat-paid-cta-btn"
                  className="px-5 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-black rounded-xl font-extrabold hover:scale-105 transition-all flex items-center gap-2 shadow-lg shadow-amber-500/30 border-2 border-amber-300"
                >
                  <Sparkles className="w-4 h-4" />
                  10-min VIP Session · 200 AT
                </button>
                <GlowButton onClick={() => setCurrentView('passes')} variant="gold">
                  Buy Pass <ChevronRight className="w-5 h-5 inline" />
                </GlowButton>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // PASSES VIEW
  if (currentView === 'passes') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setCurrentView('avatars')} className="text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Choose Your Pass</h1>
            <p className="text-gray-400 text-sm">Select a consultation pass</p>
          </div>
        </div>
        
        {/* Selected Avatar */}
        {selectedAvatar && (
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 flex items-center gap-4 max-w-xl mx-auto">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Bot className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-white font-bold">{selectedAvatar.display_name}</p>
              <p className="text-gray-400 text-sm">{selectedAvatar.description}</p>
            </div>
          </div>
        )}
        
        {/* Passes Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {passes.map(pass => (
            <PassCard
              key={pass.pass_type}
              pass={pass}
              onSelect={(p) => {
                setSelectedPass(p);
                setCurrentView('payment');
              }}
            />
          ))}
        </div>
        
        {/* Existing Passes */}
        {confirmedPasses.length > 0 && (
          <div className="max-w-5xl mx-auto mt-8">
            <h3 className="text-xl font-bold text-white mb-4">Or Use Existing Pass</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {confirmedPasses.map(pass => (
                <div key={pass.id} className="bg-slate-800/50 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-white capitalize">{pass.pass_type} Pass</p>
                    <p className="text-gray-400 text-sm">{pass.minutes_remaining?.toFixed(1)} minutes remaining</p>
                  </div>
                  <GlowButton onClick={() => startSession(pass.id)} variant="secondary">
                    Use This Pass
                  </GlowButton>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // PAYMENT VIEW
  if (currentView === 'payment') {
    const passPrice = selectedPass?.price || 0;
    const halfPrice = passPrice * 0.5;
    const canUseWallet = walletBalance >= passPrice;
    const canUseHybrid = walletBalance >= halfPrice && commissionBalance >= halfPrice;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setCurrentView('passes')} className="text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Complete Payment</h1>
            <p className="text-gray-400 text-sm">Choose your payment method</p>
          </div>
        </div>
        
        <div className="max-w-lg mx-auto">
          {/* Order Summary */}
          <div className="bg-slate-800/50 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-bold text-white mb-4">Order Summary</h3>
            <div className="flex justify-between mb-2">
              <span className="text-gray-400">Pass Type</span>
              <span className="text-white font-bold capitalize">{selectedPass?.pass_type} Pass</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-400">Duration</span>
              <span className="text-white">{selectedPass?.duration_minutes === 999999 ? '1 Session' : `${selectedPass?.duration_minutes} Min Session`}</span>
            </div>
            <hr className="border-slate-700 my-4" />
            <div className="flex justify-between">
              <span className="text-white font-bold">Total Amount</span>
              <span className="text-2xl font-black text-amber-400">₹{passPrice}</span>
            </div>
          </div>
          
          {/* Wallet Balances */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-500/30">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-5 h-5 text-green-400" />
                <span className="text-gray-300 text-sm">Cash Wallet</span>
              </div>
              <span className="text-xl font-bold text-green-400">₹{walletBalance.toFixed(2)}</span>
            </div>
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300 text-sm">Commission</span>
              </div>
              <span className="text-xl font-bold text-purple-400">₹{commissionBalance.toFixed(2)}</span>
            </div>
          </div>
          
          {/* Payment Method Selection */}
          <div className="mb-6">
            <label className="text-white font-medium mb-3 block">Select Payment Method</label>
            <div className="space-y-3">
              {/* 100% Cash Wallet */}
              <div
                onClick={() => canUseWallet && setPaymentMethod('wallet')}
                className={`
                  p-4 rounded-xl border cursor-pointer transition-all
                  ${paymentMethod === 'wallet' ? 'bg-green-500/20 border-green-500' : 'bg-slate-800/50 border-slate-700'}
                  ${!canUseWallet ? 'opacity-50 cursor-not-allowed' : 'hover:border-green-500/50'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-white font-medium">100% Cash Wallet</p>
                      <p className="text-green-400 text-sm">✓ Instant activation</p>
                    </div>
                  </div>
                  <span className="text-green-400 font-bold">₹{passPrice}</span>
                </div>
                {!canUseWallet && <p className="text-red-400 text-xs mt-2">Need ₹{passPrice} in cash wallet</p>}
              </div>
              
              {/* 50% Cash + 50% Commission */}
              <div
                onClick={() => canUseHybrid && setPaymentMethod('hybrid')}
                className={`
                  p-4 rounded-xl border cursor-pointer transition-all
                  ${paymentMethod === 'hybrid' ? 'bg-purple-500/20 border-purple-500' : 'bg-slate-800/50 border-slate-700'}
                  ${!canUseHybrid ? 'opacity-50 cursor-not-allowed' : 'hover:border-purple-500/50'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center">
                      <Wallet className="w-4 h-4 text-green-400" />
                      <span className="text-gray-400 mx-1">+</span>
                      <Crown className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">50% Cash + 50% Commission</p>
                      <p className="text-purple-400 text-sm">✓ Instant activation</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-green-400 font-bold">₹{halfPrice}</span>
                    <span className="text-gray-400 mx-1">+</span>
                    <span className="text-purple-400 font-bold">₹{halfPrice}</span>
                  </div>
                </div>
                {!canUseHybrid && (
                  <p className="text-red-400 text-xs mt-2">
                    Need ₹{halfPrice} in each wallet
                  </p>
                )}
              </div>
              
              {/* Manual UPI Payment */}
              <div
                onClick={() => setPaymentMethod('manual')}
                className={`
                  p-4 rounded-xl border cursor-pointer transition-all
                  ${paymentMethod === 'manual' ? 'bg-amber-500/20 border-amber-500' : 'bg-slate-800/50 border-slate-700'}
                  hover:border-amber-500/50
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PhoneCall className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-white font-medium">UPI / QR Payment</p>
                      <p className="text-amber-400 text-sm">⏳ Admin approval needed</p>
                    </div>
                  </div>
                  <span className="text-amber-400 font-bold">₹{passPrice}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Manual Payment Instructions (only show if manual selected) */}
          {paymentMethod === 'manual' && (
            <>
              <div className="bg-gradient-to-br from-amber-600/20 to-orange-600/20 rounded-xl p-6 mb-6 border border-amber-500/30">
                <h3 className="text-lg font-bold text-white mb-4">Payment Instructions</h3>
                <ol className="space-y-3 text-gray-300">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500 text-black flex items-center justify-center text-sm font-bold">1</span>
                    <span>Pay ₹{passPrice} via UPI or scan QR code</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500 text-black flex items-center justify-center text-sm font-bold">2</span>
                    <span>Copy the UTR/Transaction Reference Number</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500 text-black flex items-center justify-center text-sm font-bold">3</span>
                    <span>Paste it below and submit</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500 text-black flex items-center justify-center text-sm font-bold">4</span>
                    <span>Wait for admin confirmation</span>
                  </li>
                </ol>
              </div>
              
              <div className="mb-6">
                <label className="text-white font-medium mb-2 block">Payment Reference / UTR Number</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  placeholder="Enter UTR or transaction reference"
                  className="w-full px-4 py-3 bg-slate-800 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                />
              </div>
            </>
          )}
          
          {/* Coupon Code Section */}
          <div className="bg-slate-800/30 rounded-xl p-4 mb-6 border border-slate-700">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-pink-400" />
              <span className="text-white font-medium">Have a Coupon?</span>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg border border-slate-700 focus:border-pink-500"
              />
              <button
                onClick={handleRedeemCoupon}
                disabled={loading || !couponCode.trim()}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-pink-400"
              >
                Apply
              </button>
            </div>
          </div>
          
          {/* Submit Button */}
          <GlowButton
            onClick={handlePurchasePass}
            disabled={loading || (paymentMethod === 'manual' && !paymentRef.trim())}
            variant="gold"
            className="w-full"
          >
            {loading ? 'Processing...' : paymentMethod === 'manual' ? 'Submit for Approval' : 'Pay & Activate Now'}
          </GlowButton>
        </div>
      </div>
    );
  }
  
  // CONSULTATION VIEW
  if (currentView === 'consultation') {
    const isTimeLow = sessionTimeRemaining <= 5;
    const isTimeVeryLow = sessionTimeRemaining <= 2;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex flex-col">
        {/* Header */}
        <div className="bg-slate-800/50 backdrop-blur-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={endSession} className="text-white">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <AnimatedAvatar avatar={selectedAvatar} isSpeaking={isSpeaking || loading} size="sm" />
            <div>
              <p className="text-white font-bold">{selectedAvatar?.display_name}</p>
              <p className={`text-xs ${isSpeaking ? 'text-amber-400' : 'text-green-400'}`}>
                {isSpeaking ? '🎙️ Speaking...' : '● Online'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Session Timer */}
            <div className={`
              flex items-center gap-2 px-4 py-2 rounded-full font-mono
              ${isTimeVeryLow ? 'bg-red-500/30 border border-red-500 animate-pulse' : 
                isTimeLow ? 'bg-amber-500/30 border border-amber-500' : 
                'bg-slate-700'}
            `}>
              <Clock className={`w-5 h-5 ${isTimeVeryLow ? 'text-red-400' : isTimeLow ? 'text-amber-400' : 'text-green-400'}`} />
              <span className={`text-lg font-bold ${isTimeVeryLow ? 'text-red-400' : isTimeLow ? 'text-amber-400' : 'text-white'}`}>
                {formatTime(sessionTimeRemaining)}
              </span>
              <span className="text-gray-400 text-xs">/ {sessionTimeLimit}m</span>
            </div>
            {/* Voice Toggle with Options */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`p-2 rounded-full ${voiceEnabled ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-gray-400'}`}
                title={voiceEnabled ? 'Voice On' : 'Voice Off'}
                data-testid="voice-toggle-btn"
              >
                {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
              {voiceEnabled && (
                <select
                  value={voiceOption}
                  onChange={(e) => setVoiceOption(e.target.value)}
                  className="bg-slate-700 text-white px-2 py-1 rounded-lg text-xs hidden sm:block"
                  title="Voice Type"
                  data-testid="voice-option-select"
                >
                  <option value="elevenlabs">🎤 Custom</option>
                  <option value="openai">🤖 AI</option>
                  <option value="auto">✨ Auto</option>
                </select>
              )}
            </div>
            {/* Video Call Button */}
            <button
              onClick={startVideoCallMode}
              className={`p-2 rounded-full transition-all ${videoCallActive 
                ? 'bg-green-500 text-white animate-pulse' 
                : 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 hover:from-purple-500/30 hover:to-pink-500/30'
              }`}
              title={videoCallActive ? 'Video Call Active' : 'Start Video Call'}
              data-testid="video-call-btn"
            >
              <Video className="w-5 h-5" />
            </button>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-slate-700 text-white px-2 py-1 rounded-lg text-sm"
              data-testid="language-selector"
            >
              <option value="hi">हिंदी (Hindi)</option>
              <option value="en">English</option>
              <option value="ta">தமிழ் (Tamil)</option>
              <option value="te">తెలుగు (Telugu)</option>
              <option value="bn">বাংলা (Bengali)</option>
              <option value="mr">मराठी (Marathi)</option>
              <option value="gu">ગુજરાતી (Gujarati)</option>
              <option value="kn">ಕನ್ನಡ (Kannada)</option>
              <option value="ml">മലയാളം (Malayalam)</option>
              <option value="pa">ਪੰਜਾਬੀ (Punjabi)</option>
              <option value="or">ଓଡ଼ିଆ (Odia)</option>
              <option value="as">অসমীয়া (Assamese)</option>
            </select>
            <button
              onClick={endSession}
              className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30"
            >
              End Session
            </button>
            {/* Export Full Chat Button */}
            {messages.length > 0 && activeSession && (
              <button
                onClick={() => exportToPDF(null, null, true, activeSession.id)}
                disabled={exportingMessage === 'pdf'}
                className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/30 flex items-center gap-1"
                title="Export full conversation as PDF"
              >
                <Download className="w-3 h-3" /> Export Chat
              </button>
            )}
          </div>
        </div>
        
        {/* Time Warning Banner */}
        {isTimeLow && (
          <div className={`px-4 py-2 text-center ${isTimeVeryLow ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
            {isTimeVeryLow 
              ? `⚠️ Session ending in ${formatTime(sessionTimeRemaining)}! Save your conversation.`
              : `⏰ ${Math.ceil(sessionTimeRemaining)} minutes remaining in your session`
            }
          </div>
        )}
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <AnimatedAvatar avatar={selectedAvatar} isSpeaking={false} size="xl" />
              <p className="text-white text-lg font-bold mb-2 mt-6">
                {selectedAvatar?.personality?.greeting_format?.replace('{name}', 'Friend') || `Welcome! I am ${selectedAvatar?.display_name}`}
              </p>
              <p className="text-gray-400">Start the conversation by typing your message below</p>
              <p className="text-amber-400 text-sm mt-2">Session: {sessionTimeLimit} minutes</p>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start gap-3'}`}
            >
              {msg.role === 'assistant' && (
                <AnimatedAvatar 
                  avatar={selectedAvatar} 
                  isSpeaking={isSpeaking && idx === messages.length - 1} 
                  size="sm" 
                />
              )}
              <div
                className={`
                  max-w-[70%] p-4 rounded-2xl relative group
                  ${msg.role === 'user' 
                    ? 'bg-amber-500 text-black rounded-br-none' 
                    : 'bg-slate-800 text-white rounded-bl-none'}
                `}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
                
                {/* Action buttons for AI messages */}
                {msg.role === 'assistant' && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {msg.audio && voiceEnabled && (
                      <button
                        onClick={() => playAudio(msg.audio)}
                        className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
                      >
                        <Volume2 className="w-3 h-3" /> Play
                      </button>
                    )}
                    
                    {/* Export Buttons */}
                    <button
                      onClick={() => exportToPDF(msg.content, messages[idx-1]?.content)}
                      disabled={exportingMessage === 'pdf'}
                      className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100 px-2 py-1 bg-slate-700 rounded"
                      title="Download as PDF"
                    >
                      <FileText className="w-3 h-3" /> PDF
                    </button>
                    <button
                      onClick={() => exportToAudio(msg.content)}
                      disabled={exportingMessage === 'audio'}
                      className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100 px-2 py-1 bg-slate-700 rounded"
                      title="Download as Audio"
                    >
                      <Volume2 className="w-3 h-3" /> MP3
                    </button>
                    <button
                      onClick={() => exportToVideo(msg.content)}
                      disabled={exportingMessage === 'video'}
                      className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100 px-2 py-1 bg-slate-700 rounded"
                      title="Export as Video (with avatar)"
                    >
                      <Video className="w-3 h-3" /> Video
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start gap-3">
              <AnimatedAvatar avatar={selectedAvatar} isSpeaking={true} size="sm" />
              <div className="bg-slate-800 text-white p-4 rounded-2xl rounded-bl-none">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* File Upload Modal */}
        {showFileUpload && (
          <div className="absolute bottom-20 left-4 right-4 bg-slate-800 rounded-2xl p-4 border border-slate-700 shadow-xl z-20">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-bold">Upload File for Analysis</h3>
              <button onClick={() => setShowFileUpload(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  fileInputRef.current?.click();
                  fileInputRef.current.accept = 'image/*';
                }}
                className="flex flex-col items-center gap-2 p-4 bg-blue-500/20 rounded-xl hover:bg-blue-500/30 transition-all"
              >
                <ImageIcon className="w-8 h-8 text-blue-400" />
                <span className="text-white text-sm">Image</span>
              </button>
              <button
                onClick={() => {
                  fileInputRef.current?.click();
                  fileInputRef.current.accept = 'audio/*';
                }}
                className="flex flex-col items-center gap-2 p-4 bg-green-500/20 rounded-xl hover:bg-green-500/30 transition-all"
              >
                <Mic className="w-8 h-8 text-green-400" />
                <span className="text-white text-sm">Audio</span>
              </button>
              <button
                onClick={() => {
                  fileInputRef.current?.click();
                  fileInputRef.current.accept = 'video/*';
                }}
                className="flex flex-col items-center gap-2 p-4 bg-purple-500/20 rounded-xl hover:bg-purple-500/30 transition-all"
              >
                <Video className="w-8 h-8 text-purple-400" />
                <span className="text-white text-sm">Video</span>
              </button>
              <button
                onClick={() => {
                  fileInputRef.current?.click();
                  fileInputRef.current.accept = '.pdf,.txt,.doc,.docx';
                }}
                className="flex flex-col items-center gap-2 p-4 bg-amber-500/20 rounded-xl hover:bg-amber-500/30 transition-all"
              >
                <FileText className="w-8 h-8 text-amber-400" />
                <span className="text-white text-sm">Document</span>
              </button>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-gray-400 text-xs text-center mb-3">Or use camera</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => { setShowFileUpload(false); startCamera(); }}
                  className="flex flex-col items-center gap-1 p-3 bg-pink-500/20 rounded-xl hover:bg-pink-500/30 transition-all"
                >
                  <Camera className="w-6 h-6 text-pink-400" />
                  <span className="text-white text-xs">Selfie</span>
                </button>
                <button
                  onClick={() => { setShowFileUpload(false); startCamera(); }}
                  className="flex flex-col items-center gap-1 p-3 bg-orange-500/20 rounded-xl hover:bg-orange-500/30 transition-all"
                >
                  <Hand className="w-6 h-6 text-orange-400" />
                  <span className="text-white text-xs">Palm</span>
                </button>
                <button
                  onClick={() => { setShowFileUpload(false); startCamera(); }}
                  className="flex flex-col items-center gap-1 p-3 bg-cyan-500/20 rounded-xl hover:bg-cyan-500/30 transition-all"
                >
                  <User className="w-6 h-6 text-cyan-400" />
                  <span className="text-white text-xs">Face</span>
                </button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleFileUpload(e.target.files[0]);
                }
              }}
            />
          </div>
        )}
        
        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
            <div className="flex justify-between items-center p-4">
              <h3 className="text-white font-bold">Camera Capture</h3>
              <button onClick={stopCamera} className="text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                className="max-w-full max-h-[60vh] rounded-xl"
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <div className="p-4 space-y-3">
              <p className="text-gray-400 text-center text-sm">Select analysis type:</p>
              <div className="grid grid-cols-5 gap-2">
                <button
                  onClick={() => capturePhoto('general')}
                  className="flex flex-col items-center gap-1 p-3 bg-blue-500/20 rounded-xl hover:bg-blue-500/30"
                >
                  <Camera className="w-6 h-6 text-blue-400" />
                  <span className="text-white text-xs">General</span>
                </button>
                <button
                  onClick={() => capturePhoto('palm')}
                  className="flex flex-col items-center gap-1 p-3 bg-orange-500/20 rounded-xl hover:bg-orange-500/30"
                >
                  <Hand className="w-6 h-6 text-orange-400" />
                  <span className="text-white text-xs">Palm</span>
                </button>
                <button
                  onClick={() => capturePhoto('face')}
                  className="flex flex-col items-center gap-1 p-3 bg-cyan-500/20 rounded-xl hover:bg-cyan-500/30"
                >
                  <User className="w-6 h-6 text-cyan-400" />
                  <span className="text-white text-xs">Face</span>
                </button>
                <button
                  onClick={() => capturePhoto('health')}
                  className="flex flex-col items-center gap-1 p-3 bg-green-500/20 rounded-xl hover:bg-green-500/30"
                >
                  <Heart className="w-6 h-6 text-green-400" />
                  <span className="text-white text-xs">Health</span>
                </button>
                <button
                  onClick={() => capturePhoto('aura')}
                  className="flex flex-col items-center gap-1 p-3 bg-purple-500/20 rounded-xl hover:bg-purple-500/30"
                >
                  <Sparkles className="w-6 h-6 text-purple-400" />
                  <span className="text-white text-xs">Aura</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Input */}
        <div className="p-4 bg-slate-800/50 backdrop-blur-lg">
          <div className="flex gap-2">
            {/* File Upload Button */}
            <button
              onClick={() => setShowFileUpload(!showFileUpload)}
              disabled={uploadingFile}
              className="p-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all disabled:opacity-50"
              data-testid="file-upload-btn"
            >
              {uploadingFile ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Paperclip className="w-5 h-5" />
              )}
            </button>
            {/* Voice Recorder */}
            <VoiceRecorder
              onTranscription={(text) => {
                setInputMessage(text);
                toast.success('Voice transcribed!', { duration: 1500 });
              }}
              language={language}
              disabled={loading}
            />
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={language === 'hi' ? 'बोलें या लिखें...' : 'Speak or type...'}
              className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
              data-testid="consultation-chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !inputMessage.trim()}
              className="px-6 py-3 bg-amber-500 text-black rounded-xl font-bold disabled:opacity-50 hover:bg-amber-400 transition-all"
              data-testid="consultation-chat-send"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // FREE TRIAL VIEW
  if (currentView === 'trial') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-900/20 to-slate-900 flex flex-col">
        {/* Header */}
        <div className="bg-slate-800/50 backdrop-blur-lg p-3 sm:p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => setCurrentView('avatars')} className="text-white">
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <AnimatedAvatar avatar={selectedAvatar} isSpeaking={loading || isSpeaking} size="sm" />
            <div>
              <p className="text-white font-bold text-sm sm:text-base">{selectedAvatar?.display_name}</p>
              <p className="text-green-400 text-xs">🎁 Free Trial</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Voice Toggle */}
            <button
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className={`p-2 rounded-full ${voiceEnabled ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-gray-400'}`}
              title={voiceEnabled ? 'Voice On' : 'Voice Off'}
              data-testid="trial-voice-toggle"
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
            {/* Video Call Button */}
            <button
              onClick={startVideoCallMode}
              className={`p-2 rounded-full transition-all ${videoCallActive 
                ? 'bg-green-500 text-white animate-pulse' 
                : 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-purple-400 hover:from-purple-500/40 hover:to-pink-500/40'
              }`}
              title="Video Call Mode"
              data-testid="trial-video-call-btn"
            >
              <Video className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            {/* Trial Messages Counter */}
            <div className="flex items-center gap-1 sm:gap-2 bg-green-500/20 px-2 sm:px-3 py-1 rounded-full border border-green-500/30">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
              <span className="text-green-400 text-xs sm:text-sm font-bold">
                {trialStatus.messages_remaining}/{trialStatus.max_messages}
              </span>
            </div>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-slate-700 text-white px-2 py-1 rounded-lg text-xs sm:text-sm hidden sm:block"
            >
              <option value="hi">Hindi</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {trialMessages.length === 0 && (
            <div className="text-center py-12">
              <AnimatedAvatar avatar={selectedAvatar} isSpeaking={false} size="xl" />
              <p className="text-white text-lg font-bold mb-2 mt-6">
                🎁 Free Trial with {selectedAvatar?.display_name}
              </p>
              <p className="text-gray-400 mb-4">
                You have <span className="text-green-400 font-bold">{trialStatus.messages_remaining} free messages</span> to try!
              </p>
              <p className="text-gray-500 text-sm">Start the conversation below</p>
            </div>
          )}
          
          {trialMessages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start gap-3'}`}
            >
              {msg.role === 'assistant' && (
                <AnimatedAvatar avatar={selectedAvatar} isSpeaking={false} size="sm" />
              )}
              <div
                className={`
                  max-w-[70%] p-4 rounded-2xl
                  ${msg.role === 'user' 
                    ? 'bg-green-500 text-white rounded-br-none' 
                    : 'bg-slate-800 text-white rounded-bl-none'}
                `}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start gap-3">
              <AnimatedAvatar avatar={selectedAvatar} isSpeaking={true} size="sm" />
              <div className="bg-slate-800 text-white p-4 rounded-2xl rounded-bl-none">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          {/* Trial Expired Message */}
          {trialStatus.messages_remaining <= 0 && (
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-6 text-center border border-amber-500/30">
              <Crown className="w-12 h-12 text-amber-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white mb-2">Free Trial Ended!</h3>
              <p className="text-gray-300 mb-4">
                Loved the conversation? Purchase a pass to continue your spiritual journey!
              </p>
              <GlowButton onClick={() => setCurrentView('passes')} variant="gold">
                <Crown className="w-5 h-5 inline mr-2" />
                Buy Pass Now
              </GlowButton>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="p-4 bg-slate-800/50 backdrop-blur-lg">
          {trialStatus.messages_remaining > 0 ? (
            <div className="flex gap-2">
              {/* Voice Recorder */}
              <VoiceRecorder
                onTranscription={(text) => {
                  setInputMessage(text);
                  toast.success('Voice transcribed!', { duration: 1500 });
                }}
                language={language}
                disabled={loading}
              />
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendTrialMessage()}
                placeholder={language === 'hi' ? 'बोलें या लिखें...' : 'Speak or type...'}
                className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-green-500"
                data-testid="free-trial-chat-input"
              />
              <button
                onClick={sendTrialMessage}
                disabled={loading || !inputMessage.trim()}
                className="px-6 py-3 bg-green-500 text-white rounded-xl font-bold disabled:opacity-50 hover:bg-green-400 transition-all"
                data-testid="free-trial-chat-send"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-400 mb-3">Trial ended - Purchase a pass to continue</p>
              <GlowButton onClick={() => setCurrentView('passes')} variant="gold" className="w-full">
                View Passes
              </GlowButton>
            </div>
          )}
        </div>
        
        {/* Video Call Mode for Trial */}
        <VideoCallMode
          avatar={selectedAvatar}
          isActive={videoCallActive}
          onClose={() => {
            setVideoCallActive(false);
            setCurrentAudioSrc(null);
            setIsAudioPlaying(false);
          }}
          audioSrc={currentAudioSrc}
          isPlaying={isAudioPlaying}
          onAudioEnd={handleVideoCallAudioEnd}
          token={token}
        />
      </div>
    );
  }
  
  // REPORTS VIEW
  if (currentView === 'reports') {
    const reportTypes = [
      { type: 'numerology_name', name: 'Name Numerology', icon: User, description: 'Analyze your name vibrations', color: 'purple' },
      { type: 'numerology_mobile', name: 'Mobile Number Numerology', icon: Phone, description: 'Vibration of your mobile number', color: 'blue' },
      { type: 'numerology_mobile_name', name: 'Mobile + Name Numerology', icon: PhoneCall, description: 'Is your number lucky for your name', color: 'indigo' },
      { type: 'numerology_dob', name: 'DOB Numerology', icon: Calendar, description: 'Birth date life path', color: 'amber' },
      { type: 'kundali', name: 'Kundali Report', icon: Star, description: 'Complete birth chart', color: 'orange' },
      { type: 'palmistry', name: 'Palm Reading', icon: Hand, description: 'AI palm analysis', color: 'pink' },
      { type: 'face_reading', name: 'Face Reading', icon: User, description: 'AI face analysis (मुखाकृति)', color: 'indigo' },
      { type: 'match_making', name: 'Match Making', icon: Heart, description: 'Compatibility report', color: 'red' },
      { type: 'daily_horoscope', name: 'Daily Horoscope', icon: Sparkles, description: 'Today\'s prediction', color: 'cyan' },
    ];
    
    const colorClasses = {
      purple: 'from-purple-500 to-purple-700 border-purple-500/30',
      blue: 'from-blue-500 to-blue-700 border-blue-500/30',
      amber: 'from-amber-500 to-amber-700 border-amber-500/30',
      orange: 'from-orange-500 to-orange-700 border-orange-500/30',
      pink: 'from-pink-500 to-pink-700 border-pink-500/30',
      red: 'from-red-500 to-red-700 border-red-500/30',
      cyan: 'from-cyan-500 to-cyan-700 border-cyan-500/30',
      indigo: 'from-indigo-500 to-indigo-700 border-indigo-500/30',
    };
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => { setCurrentView('home'); setSelectedReportType(null); }} className="text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">AI Reports</h1>
            <p className="text-gray-400 text-sm">Generate Numerology, Kundali, Palmistry & Face Reading Reports</p>
          </div>
        </div>
        
        {/* Diamond Pass Status */}
        {diamondPasses.length > 0 ? (
          <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-xl p-4 mb-6 border border-cyan-500/30 max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Diamond className="w-8 h-8 text-cyan-400" />
                <div>
                  <p className="font-bold text-white">Diamond Pass Active</p>
                  <p className="text-gray-400 text-sm">
                    {diamondPasses[0].reports_limit - diamondPasses[0].reports_used} of {diamondPasses[0].reports_limit} reports remaining
                  </p>
                </div>
              </div>
              <div className="text-4xl font-black text-cyan-400">
                {diamondPasses[0].reports_limit - diamondPasses[0].reports_used}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-800/50 rounded-xl p-6 mb-6 text-center max-w-3xl mx-auto">
            <Diamond className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-white font-bold mb-2">Diamond Pass Required</p>
            <p className="text-gray-400 text-sm mb-4">Purchase a Diamond Pass to generate AI reports</p>
            <GlowButton onClick={() => setCurrentView('passes')} variant="diamond">
              Buy Diamond Pass - ₹990
            </GlowButton>
          </div>
        )}
        
        {/* Report Type Selection or Form */}
        {!selectedReportType ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {reportTypes.map(report => (
              <div
                key={report.type}
                onClick={() => diamondPasses.length > 0 && setSelectedReportType(report.type)}
                className={`
                  bg-gradient-to-br ${colorClasses[report.color]} 
                  rounded-xl p-5 cursor-pointer transition-all border
                  ${diamondPasses.length > 0 ? 'hover:scale-105' : 'opacity-50 cursor-not-allowed'}
                `}
                data-testid={`report-${report.type}`}
              >
                <report.icon className="w-10 h-10 text-white mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{report.name}</h3>
                <p className="text-white/80 text-sm">{report.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-lg mx-auto">
            {/* Report Form */}
            <div className="bg-slate-800/50 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-white mb-4">
                {reportTypes.find(r => r.type === selectedReportType)?.name}
              </h3>
              
              {/* Language Selector - Prominent at top */}
              <div className="mb-6 p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl border border-amber-500/30">
                <label className="text-amber-400 font-bold mb-3 block flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Select Report Language
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {reportLanguages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => setReportLanguage(lang.code)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        reportLanguage === lang.code 
                          ? 'bg-amber-500 text-black' 
                          : 'bg-slate-700 text-white hover:bg-slate-600'
                      }`}
                    >
                      {lang.native}
                    </button>
                  ))}
                </div>
                <p className="text-amber-300/70 text-xs mt-2">
                  Your report will be generated in {reportLanguages.find(l => l.code === reportLanguage)?.native}
                </p>
              </div>
              
              {/* Common Fields */}
              {['numerology_name', 'numerology_mobile_name', 'numerology_dob', 'kundali', 'palmistry', 'face_reading', 'match_making'].includes(selectedReportType) && (
                <div className="mb-4">
                  <label className="text-white font-medium mb-2 block">Full Name</label>
                  <input
                    type="text"
                    value={reportForm.full_name || ''}
                    onChange={(e) => setReportForm({...reportForm, full_name: e.target.value})}
                    placeholder="Enter full name"
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                  />
                </div>
              )}
              
              {/* Mobile Number */}
              {['numerology_mobile', 'numerology_mobile_name'].includes(selectedReportType) && (
                <div className="mb-4">
                  <label className="text-white font-medium mb-2 block">Mobile Number</label>
                  <input
                    type="tel"
                    value={reportForm.mobile_number || ''}
                    onChange={(e) => setReportForm({...reportForm, mobile_number: e.target.value})}
                    placeholder="Enter 10-digit mobile number"
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                  />
                </div>
              )}
              
              {/* DOB Fields */}
              {['numerology_dob', 'kundali', 'match_making'].includes(selectedReportType) && (
                <div className="mb-4">
                  <label className="text-white font-medium mb-2 block">Date of Birth</label>
                  <input
                    type="date"
                    value={reportForm.date_of_birth || ''}
                    onChange={(e) => setReportForm({...reportForm, date_of_birth: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                  />
                </div>
              )}
              
              {/* Kundali / Match Making: birth time and place */}
              {['kundali', 'match_making'].includes(selectedReportType) && (
                <>
                  <div className="mb-4">
                    <label className="text-white font-medium mb-2 block">{selectedReportType === 'match_making' ? 'Your Birth Time' : 'Birth Time'}</label>
                    <input
                      type="time"
                      value={reportForm.birth_time || ''}
                      onChange={(e) => setReportForm({...reportForm, birth_time: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-white font-medium mb-2 block">{selectedReportType === 'match_making' ? 'Your Birth Place' : 'Birth Place'}</label>
                    <input
                      type="text"
                      value={reportForm.birth_place || ''}
                      onChange={(e) => setReportForm({...reportForm, birth_place: e.target.value})}
                      placeholder="City, State, Country"
                      className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                    />
                  </div>
                </>
              )}
              
              {/* Palm Image Upload */}
              {selectedReportType === 'palmistry' && (
                <div className="mb-4">
                  <label className="text-white font-medium mb-2 block">Palm Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const base64 = ev.target.result.split(',')[1];
                          setReportForm({...reportForm, palm_image_base64: base64});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700"
                  />
                  <p className="text-gray-500 text-xs mt-1">Upload a clear photo of your palm</p>
                </div>
              )}
              
              {/* Face Reading Image Upload */}
              {selectedReportType === 'face_reading' && (
                <div className="mb-4">
                  <label className="text-white font-medium mb-2 block">Face Photo (Clear Selfie)</label>
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const base64 = ev.target.result.split(',')[1];
                          setReportForm({...reportForm, face_image_base64: base64});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700"
                  />
                  <p className="text-gray-500 text-xs mt-1">Upload a clear front-facing photo for accurate face reading (मुखाकृति विज्ञान)</p>
                </div>
              )}
              
              {/* Match Making Partner Fields */}
              {selectedReportType === 'match_making' && (
                <>
                  <hr className="border-slate-700 my-4" />
                  <h4 className="text-white font-bold mb-3">Partner Details</h4>
                  <div className="mb-4">
                    <label className="text-white font-medium mb-2 block">Partner Name</label>
                    <input
                      type="text"
                      value={reportForm.partner_name || ''}
                      onChange={(e) => setReportForm({...reportForm, partner_name: e.target.value})}
                      placeholder="Enter partner's full name"
                      className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-white font-medium mb-2 block">Partner DOB</label>
                    <input
                      type="date"
                      value={reportForm.partner_dob || ''}
                      onChange={(e) => setReportForm({...reportForm, partner_dob: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-white font-medium mb-2 block">Partner Birth Time</label>
                    <input
                      type="time"
                      value={reportForm.partner_birth_time || ''}
                      onChange={(e) => setReportForm({...reportForm, partner_birth_time: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-white font-medium mb-2 block">Partner Birth Place</label>
                    <input
                      type="text"
                      value={reportForm.partner_birth_place || ''}
                      onChange={(e) => setReportForm({...reportForm, partner_birth_place: e.target.value})}
                      placeholder="City, State, Country"
                      className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                    />
                  </div>
                </>
              )}
              
              {/* Zodiac Sign */}
              {selectedReportType === 'daily_horoscope' && (
                <div className="mb-4">
                  <label className="text-white font-medium mb-2 block">Zodiac Sign</label>
                  <select
                    value={reportForm.zodiac_sign || ''}
                    onChange={(e) => setReportForm({...reportForm, zodiac_sign: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                  >
                    <option value="">Select Zodiac Sign</option>
                    {['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'].map(sign => (
                      <option key={sign} value={sign}>{sign}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setSelectedReportType(null); setReportForm({}); }}
                  className="flex-1 px-4 py-3 bg-slate-700 text-white rounded-xl font-bold hover:bg-slate-600 transition-all"
                >
                  Cancel
                </button>
                <GlowButton
                  onClick={() => generateReport(selectedReportType)}
                  disabled={generatingReport}
                  variant="diamond"
                  className="flex-1"
                >
                  {generatingReport ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </span>
                  ) : 'Generate Report'}
                </GlowButton>
              </div>
            </div>
          </div>
        )}
        
        {/* My Reports */}
        {userReports.length > 0 && (
          <div className="max-w-5xl mx-auto mt-8">
            <h3 className="text-xl font-bold text-white mb-4">My Reports</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {userReports.map(report => (
                <div key={report.id} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-white">{report.title || report.report_type.replace(/_/g, ' ').toUpperCase()}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-400 text-sm">
                          {new Date(report.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                        {report.language_name && (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                            {report.language_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {(() => {
                    // Handle multiple content shapes: {analysis: "..."} OR raw string OR nested
                    const raw = report.content?.analysis || report.content?.text || report.content;
                    const text = typeof raw === 'string' ? raw : (raw ? JSON.stringify(raw).slice(0, 500) : '');
                    if (!text) return null;
                    return (
                      <p className="text-gray-300 text-sm mt-2 line-clamp-3">
                        {text.substring(0, 200)}...
                      </p>
                    );
                  })()}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => setViewReport(report)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-all"
                      data-testid={`view-full-${report.id}`}
                    >
                      <Eye className="w-4 h-4" />
                      View Full
                    </button>
                    <button
                      onClick={() => downloadReportPDF(report.id || report._id)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-medium hover:opacity-90 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                    <button
                      onClick={() => shareOnWhatsApp(report)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-all"
                    >
                      <FaWhatsapp className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* 👁 Full Report View Modal — puri report scroll karke padhne ke liye */}
        {viewReport && (
          <div
            className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-3"
            onClick={() => setViewReport(null)}
            data-testid="report-view-modal"
          >
            <div
              className="bg-gradient-to-br from-slate-900 via-indigo-950/80 to-slate-950 rounded-2xl border border-fuchsia-400/40 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-4 border-b border-white/10 bg-gradient-to-r from-fuchsia-500/10 to-purple-500/10">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black">
                      {(viewReport.report_type || '').replace(/_/g, ' ')}
                    </span>
                    {(viewReport.language_name || viewReport.language) && (
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-[10px] rounded-full font-bold">
                        {viewReport.language_name || viewReport.language}
                      </span>
                    )}
                    <span className="text-slate-400 text-[10px]">
                      {viewReport.created_at ? new Date(viewReport.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    </span>
                  </div>
                  <h2 className="text-lg md:text-xl font-black text-white truncate">
                    {viewReport.title || (viewReport.report_type || '').replace(/_/g, ' ').toUpperCase()}
                  </h2>
                </div>
                <button
                  onClick={() => setViewReport(null)}
                  className="ml-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white flex-shrink-0"
                  data-testid="report-view-close"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="report-view-content">
                {(() => {
                  // Robust content extraction: {analysis: string} | {text: string} | string | nested
                  const c = viewReport.content;
                  let text = '';
                  if (typeof c === 'string') text = c;
                  else if (c?.analysis && typeof c.analysis === 'string') text = c.analysis;
                  else if (c?.text && typeof c.text === 'string') text = c.text;
                  else if (c?.report && typeof c.report === 'string') text = c.report;
                  else if (c) text = JSON.stringify(c, null, 2);
                  if (!text.trim()) {
                    return <div className="text-slate-400 italic text-center py-8">Report content not available. Try downloading the PDF.</div>;
                  }
                  // Minimal markdown: **bold**, ## headings, keep newlines. XSS-safe (React escapes text).
                  const lines = text.split(/\r?\n/);
                  return (
                    <div className="text-slate-100 text-sm md:text-base leading-relaxed whitespace-pre-wrap break-words" style={{ fontFamily: '"Noto Sans Devanagari", "Segoe UI", system-ui, sans-serif' }}>
                      {lines.map((line, i) => {
                        // Headings
                        if (/^##\s+/.test(line)) return <h3 key={i} className="text-fuchsia-300 font-black text-base md:text-lg mt-3 mb-1">{line.replace(/^##\s+/, '')}</h3>;
                        if (/^#\s+/.test(line)) return <h2 key={i} className="text-amber-300 font-black text-lg md:text-xl mt-4 mb-2">{line.replace(/^#\s+/, '')}</h2>;
                        // Bold **text** rendering — split and inline
                        const parts = line.split(/(\*\*[^*]+\*\*)/g);
                        return (
                          <p key={i} className="mb-1">
                            {parts.map((p, j) => {
                              if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={j} className="text-white font-black">{p.slice(2, -2)}</strong>;
                              return <span key={j}>{p}</span>;
                            })}
                          </p>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Footer actions */}
              <div className="border-t border-white/10 p-3 space-y-2 bg-slate-950/60">
                {/* 🔊 Read Aloud controls */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="report-read-aloud-bar">
                  {!speaking ? (
                    <button
                      onClick={startReadAloud}
                      className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-black text-xs hover:opacity-90"
                      data-testid="report-read-aloud-play"
                    >
                      <Volume2 className="w-4 h-4" /> 🔊 Read Aloud
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={pauseReadAloud}
                        className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-black rounded-lg font-black text-xs"
                        data-testid="report-read-aloud-pause"
                      >
                        {paused ? '▶ Resume' : '⏸ Pause'}
                      </button>
                      <button
                        onClick={stopReadAloud}
                        className="flex items-center gap-1.5 px-3 py-2 bg-rose-500 text-white rounded-lg font-black text-xs"
                        data-testid="report-read-aloud-stop"
                      >
                        <VolumeX className="w-4 h-4" /> Stop
                      </button>
                    </>
                  )}
                  <div className="flex items-center gap-1 ml-auto">
                    <span className="text-[10px] text-slate-400 font-bold">Speed:</span>
                    {[0.85, 1.0, 1.25, 1.5].map((r) => (
                      <button
                        key={r}
                        onClick={() => {
                          setSpeechRate(r);
                          if (speaking) { stopReadAloud(); setTimeout(startReadAloud, 100); }
                        }}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-black ${speechRate === r ? 'bg-amber-500 text-black' : 'bg-white/5 text-slate-300 border border-white/10'}`}
                        data-testid={`report-read-aloud-speed-${r}`}
                      >
                        {r}×
                      </button>
                    ))}
                  </div>
                </div>
                {/* 📱 Save as Audio (tab-audio capture — Chrome/Edge desktop only) */}
                <button
                  onClick={saveAsAudio}
                  disabled={saving || speaking}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg font-black text-xs hover:opacity-90 disabled:opacity-50"
                  data-testid="report-save-audio"
                  title="Chrome/Edge desktop pe hi kaam karega — popup mein 'Share tab audio' tick karna zaroori"
                >
                  {saving ? '⏺ Recording… (audio play ho raha hai)' : '📱 Save as Audio (.webm) — Chrome/Edge only'}
                </button>
                {/* Download / Share / Close row */}
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadReportPDF(viewReport.id || viewReport._id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-bold hover:opacity-90"
                    data-testid="report-view-download"
                  >
                    <Download className="w-4 h-4" /> Download PDF
                  </button>
                  <button
                    onClick={() => shareOnWhatsApp(viewReport)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600"
                    data-testid="report-view-share"
                  >
                    <FaWhatsapp className="w-4 h-4" /> Share
                  </button>
                  <button
                    onClick={() => setViewReport(null)}
                    className="px-4 py-2 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Referral Modal */}
        {showReferralModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-700">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Refer & Earn</h2>
                <button onClick={() => setShowReferralModal(false)} className="text-gray-400 hover:text-white">
                  <ArrowLeft className="w-6 h-6" />
                </button>
              </div>
              
              {referralData ? (
                <>
                  {/* Referral Code */}
                  <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl p-4 mb-4 border border-green-500/30">
                    <p className="text-gray-400 text-sm mb-1">Your Referral Code</p>
                    <p className="text-2xl font-bold text-green-400">{referralData.referral_code}</p>
                  </div>
                  
                  {/* Share Link */}
                  <div className="mb-4">
                    <label className="text-white text-sm font-medium mb-2 block">Share Link</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={referralData.share_link}
                        className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 text-sm"
                      />
                      <button
                        onClick={copyReferralLink}
                        className="px-4 py-3 bg-amber-500 text-black rounded-xl font-bold hover:bg-amber-400 transition-all"
                        data-testid="copy-referral-link"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  {/* Share Buttons */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <button
                      onClick={shareToWhatsApp}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-500 transition-all"
                      data-testid="share-whatsapp"
                    >
                      <FaWhatsapp className="w-5 h-5" />
                      WhatsApp
                    </button>
                    <button
                      onClick={copyReferralLink}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all"
                      data-testid="copy-link-btn"
                    >
                      <Gift className="w-5 h-5" />
                      Copy Link
                    </button>
                  </div>
                  
                  {/* Referral Stats */}
                  {referralStats && (
                    <div className="bg-slate-900 rounded-xl p-4">
                      <h3 className="text-white font-bold mb-3">Your Referral Stats</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-amber-400">{referralStats.total_referrals || 0}</p>
                          <p className="text-gray-400 text-sm">Total Referrals</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-400">{referralStats.converted_trials || 0}</p>
                          <p className="text-gray-400 text-sm">Converted</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* How it works */}
                  <div className="mt-4 text-gray-400 text-sm">
                    <p className="font-medium text-white mb-2">How it works:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Share your referral link with friends</li>
                      <li>When they try free messages, you get credit</li>
                      <li>When they buy a pass, you earn commission!</li>
                    </ol>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-400">Loading referral data...</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // HEALTH INSIGHTS VIEW
  if (currentView === 'insights') {
    return (
      <HealthInsights 
        token={token}
        onClose={() => setCurrentView('health')}
      />
    );
  }
  
  // HEALTH TRENDS VIEW
  if (currentView === 'trends') {
    return (
      <HealthTrendsDashboard 
        token={token}
        onClose={() => setCurrentView('health')}
      />
    );
  }
  
  // HEALTH HISTORY VIEW
  if (currentView === 'health') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pb-20">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-lg border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <button 
              onClick={() => setCurrentView('home')} 
              className="text-white flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <h1 className="text-xl font-bold text-white">Health History</h1>
            <div className="w-20"></div>
          </div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 py-6">
          {/* Health Summary Cards */}
          {healthSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-red-400">{healthSummary.active_symptoms}</p>
                <p className="text-gray-400 text-sm">Active Symptoms</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-400">{healthSummary.total_consultations}</p>
                <p className="text-gray-400 text-sm">Consultations</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-400">{healthSummary.total_prescriptions}</p>
                <p className="text-gray-400 text-sm">Prescriptions</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">{healthMetrics.length}</p>
                <p className="text-gray-400 text-sm">Metrics Logged</p>
              </div>
            </div>
          )}
          
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-8">
            {/* Quick Health Check - Featured Button */}
            <button
              onClick={captureHealthCheckPhoto}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:opacity-90 shadow-lg shadow-purple-500/20 transition-all"
              data-testid="quick-health-check-btn"
            >
              <Camera className="w-5 h-5" /> Quick Health Check
            </button>
            {/* View Insights Button */}
            <button
              onClick={() => setCurrentView('insights')}
              className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-400 rounded-xl hover:bg-violet-500/30"
              data-testid="view-insights-btn"
            >
              <Brain className="w-4 h-4" /> AI Insights
            </button>
            {/* View Trends Button */}
            <button
              onClick={() => setCurrentView('trends')}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-xl hover:bg-indigo-500/30"
              data-testid="view-trends-btn"
            >
              <TrendingUp className="w-4 h-4" /> View Trends
            </button>
            <button
              onClick={() => setShowAddSymptom(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30"
            >
              <Plus className="w-4 h-4" /> Add Symptom
            </button>
            <button
              onClick={() => setShowAddMetric(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30"
            >
              <Plus className="w-4 h-4" /> Log Metric
            </button>
            <button
              onClick={() => setShowEditProfile(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30"
            >
              <Settings className="w-4 h-4" /> Health Profile
            </button>
          </div>
          
          {/* Active Symptoms */}
          {healthSymptoms.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-bold text-white mb-4">Active Symptoms</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {healthSymptoms.map(symptom => (
                  <div key={symptom.id} className="bg-slate-800/50 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-white">{symptom.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        symptom.severity === 'severe' ? 'bg-red-500/20 text-red-400' :
                        symptom.severity === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {symptom.severity}
                      </span>
                    </div>
                    {symptom.description && (
                      <p className="text-gray-400 text-sm mb-2">{symptom.description}</p>
                    )}
                    <div className="flex justify-between items-center mt-3">
                      <span className="text-xs text-gray-500">
                        Since: {new Date(symptom.started_date).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => resolveSymptom(symptom.id)}
                        className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" /> Mark Resolved
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Recent Health History */}
          <div>
            <h2 className="text-lg font-bold text-white mb-4">Recent History</h2>
            {healthHistory.length > 0 ? (
              <div className="space-y-4">
                {healthHistory.map(entry => (
                  <div key={entry.id} className="bg-slate-800/50 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          entry.entry_type === 'consultation' ? 'bg-blue-400' :
                          entry.entry_type === 'symptom_log' ? 'bg-red-400' :
                          'bg-green-400'
                        }`}></span>
                        <h3 className="font-bold text-white">{entry.title}</h3>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-3">{entry.content}</p>
                    {entry.avatar_name && (
                      <p className="text-xs text-amber-400 mt-2">With {entry.avatar_name}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No health history yet</p>
                <p className="text-sm">Consult with Dr. Priya to start tracking</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Add Symptom Modal */}
        {showAddSymptom && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Add Symptom</h2>
                <button onClick={() => setShowAddSymptom(false)} className="text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                addSymptom(Object.fromEntries(formData));
              }}>
                <input
                  name="name"
                  placeholder="Symptom name (e.g., Headache)"
                  required
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3"
                />
                <select name="severity" className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3">
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </select>
                <select name="category" className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3">
                  <option value="general">General</option>
                  <option value="digestive">Digestive</option>
                  <option value="respiratory">Respiratory</option>
                  <option value="skin">Skin</option>
                  <option value="mental">Mental Health</option>
                  <option value="joint_muscle">Joint/Muscle</option>
                </select>
                <textarea
                  name="description"
                  placeholder="Description (optional)"
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3 h-24"
                />
                <button type="submit" className="w-full py-3 bg-amber-500 text-black rounded-xl font-bold">
                  Add Symptom
                </button>
              </form>
            </div>
          </div>
        )}
        
        {/* Add Metric Modal */}
        {showAddMetric && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Log Health Metric</h2>
                <button onClick={() => setShowAddMetric(false)} className="text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                addMetric(Object.fromEntries(formData));
              }}>
                <select name="metric_type" className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3">
                  <option value="weight">Weight</option>
                  <option value="blood_pressure">Blood Pressure</option>
                  <option value="blood_sugar">Blood Sugar</option>
                  <option value="heart_rate">Heart Rate</option>
                  <option value="sleep_hours">Sleep Hours</option>
                  <option value="temperature">Temperature</option>
                  <option value="oxygen_level">Oxygen Level</option>
                </select>
                <input
                  name="value"
                  placeholder="Value (e.g., 72)"
                  required
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3"
                />
                <input
                  name="unit"
                  placeholder="Unit (e.g., kg, mmHg)"
                  required
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3"
                />
                <textarea
                  name="notes"
                  placeholder="Notes (optional)"
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3 h-20"
                />
                <button type="submit" className="w-full py-3 bg-amber-500 text-black rounded-xl font-bold">
                  Log Metric
                </button>
              </form>
            </div>
          </div>
        )}
        
        {/* Health Profile Modal */}
        {showEditProfile && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Health Profile</h2>
                <button onClick={() => setShowEditProfile(false)} className="text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const params = new URLSearchParams(Object.fromEntries(formData));
                try {
                  const res = await fetch(`${API_URL}/api/sankat-mochan/health/profile?${params}`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (res.ok) {
                    toast.success('Profile updated');
                    fetchHealthSummary();
                    setShowEditProfile(false);
                  }
                } catch (error) {
                  toast.error('Failed to update profile');
                }
              }}>
                <select 
                  name="blood_group" 
                  defaultValue={healthProfile?.blood_group || ''}
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
                <input
                  name="allergies"
                  defaultValue={healthProfile?.allergies?.join(', ') || ''}
                  placeholder="Allergies (comma-separated)"
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3"
                />
                <input
                  name="chronic_conditions"
                  defaultValue={healthProfile?.chronic_conditions?.join(', ') || ''}
                  placeholder="Chronic conditions (comma-separated)"
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3"
                />
                <input
                  name="current_medications"
                  defaultValue={healthProfile?.current_medications?.join(', ') || ''}
                  placeholder="Current medications (comma-separated)"
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3"
                />
                <textarea
                  name="family_history"
                  defaultValue={healthProfile?.family_history || ''}
                  placeholder="Family medical history"
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3 h-24"
                />
                <textarea
                  name="lifestyle_notes"
                  defaultValue={healthProfile?.lifestyle_notes || ''}
                  placeholder="Lifestyle notes (diet, exercise, etc.)"
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl mb-3 h-24"
                />
                <button type="submit" className="w-full py-3 bg-amber-500 text-black rounded-xl font-bold">
                  Save Profile
                </button>
              </form>
            </div>
          </div>
        )}
        
        {/* Quick Health Check Camera Modal */}
        {showQuickHealthCheck && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-lg w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Quick Health Check</h2>
                <button 
                  onClick={() => {
                    if (cameraStream) {
                      cameraStream.getTracks().forEach(track => track.stop());
                      setCameraStream(null);
                    }
                    setShowQuickHealthCheck(false);
                  }} 
                  className="text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-400 text-sm mb-4">
                Take a selfie for AI-powered health assessment. We'll analyze skin, eyes, fatigue levels, and more.
              </p>
              
              <div className="relative mb-4 rounded-xl overflow-hidden bg-slate-900">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted 
                  className="w-full aspect-[4/3] object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (cameraStream) {
                      cameraStream.getTracks().forEach(track => track.stop());
                      setCameraStream(null);
                    }
                    setShowQuickHealthCheck(false);
                  }}
                  className="flex-1 py-3 bg-slate-700 text-white rounded-xl"
                >
                  Cancel
                </button>
                <button
                  onClick={takeHealthCheckSnapshot}
                  disabled={healthCheckLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                >
                  {healthCheckLoading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Camera className="w-5 h-5" /> Capture & Analyze</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Health Check Result Modal */}
        {healthCheckResult && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Activity className="w-6 h-6 text-purple-400" />
                  Health Check Results
                </h2>
                <button onClick={() => setHealthCheckResult(null)} className="text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Wellness Score */}
              {healthCheckResult.wellness_score && (
                <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl p-4 mb-6 text-center">
                  <p className="text-gray-400 text-sm mb-1">Wellness Score</p>
                  <p className="text-4xl font-bold text-white">{healthCheckResult.wellness_score}/10</p>
                </div>
              )}
              
              {/* Full Analysis */}
              <div className="bg-slate-900/50 rounded-xl p-4 mb-4">
                <h3 className="font-bold text-white mb-2">Detailed Analysis</h3>
                <div className="text-gray-300 whitespace-pre-wrap text-sm">
                  {healthCheckResult.full_analysis}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setHealthCheckResult(null)}
                  className="flex-1 py-3 bg-slate-700 text-white rounded-xl"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    // Navigate to Dr. Priya for follow-up
                    const drPriya = avatars.find(a => a.id === 'dr_priya');
                    if (drPriya) {
                      setSelectedAvatar(drPriya);
                      setCurrentView('home');
                      setHealthCheckResult(null);
                      toast.info('Select a pass to consult with Dr. Priya about your results');
                    }
                  }}
                  className="flex-1 py-3 bg-amber-500 text-black rounded-xl font-bold"
                >
                  Consult Dr. Priya
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Video Call Mode Overlay */}
        <VideoCallMode
          avatar={selectedAvatar}
          isActive={videoCallActive}
          onClose={() => {
            setVideoCallActive(false);
            setCurrentAudioSrc(null);
            setIsAudioPlaying(false);
          }}
          audioSrc={currentAudioSrc}
          isPlaying={isAudioPlaying}
          onAudioEnd={handleVideoCallAudioEnd}
          token={token}
        />
      </div>
    );
  }
  
  return null;
};

export default SankatMochanSamadhan;
